import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { FigureRender } from "../components/FigureRender";
import FigureAssistPanel from "../components/FigureAssistPanel";
import MathLiveEditor from "../components/MathLiveEditor";
import SlateCanvasPanel from "../components/SlateCanvasPanel";
import PhotoSolutionModal from "../components/PhotoSolutionModal";
import ActionModal from "../components/ActionModal";
import { extractFigureAssistLabelHints } from "../lib/figureLabelHints";
import useInkDeviceProfile from "../hooks/useInkDeviceProfile";
import {
  createInkPointProcessor,
  getCoalescedPointerSamples
} from "../lib/inkDeviceProfile";
import { createInkPointerOwnership } from "../lib/inkPointerOwnership";
import {
  renderClusterImageData,
  updateStepClustersIncremental
} from "../lib/dynamicInkSegmenter";

import { formatLatexForDisplay } from "../lib/latexUtils";

const levelOrder = ["LOT", "MOT", "HOT"];
const LIVE_OCR_FLUSH_DELAY_MS = 700;
const DEFAULT_LANE_HEIGHT = 90;
const MIN_LANE_HEIGHT = 56;
const MIN_STAGE_WIDTH = 220;
const DIAGNOSTIC_RAIL_WIDTH = 40;
const INITIAL_LANE_COUNT = 6;
const SLATE_TRAILING_BLANK_LANES = 6;
const SLATE_SCROLL_EXTEND_BATCH = 8;
const LANE_OCR_PADDING = 16;
const LIVE_OCR_MIN_DELAY_MS = 120;
const RULER_INK_GUARD_PX = 10;
const STAGE_CONTENT_RIGHT_PAD = 24;
const SLATE_RULE_STROKE = "rgba(37, 99, 235, 0.35)";
const SLATE_RULE_STROKE_WIDTH = 1.2;
const SLATE_RULE_DASH = [2, 7];

const chapterTitles = {
  "linear-equations": "Linear Equations",
  "quadratic-functions": "Quadratic Functions",
  "coordinate-geometry": "Coordinate Geometry",
  geometry: "Geometry",
  statistics: "Statistics",
  trigonometry: "Trigonometry Basics",
  probability: "Probability & Statistics"
};

function getCollabInviteMode(value) {
  return String(value || "").includes("@") ? "email" : "username";
}

function normalizeOcrLatexForEditor(input) {
  const envPattern =
    "(?:align\\*?|gather\\*?|aligned|equation\\*?|multline\\*?|cases|array|matrix|pmatrix|bmatrix|vmatrix|smallmatrix)";
  const value = String(input || "")
    .replace(new RegExp(`\\\\begin\\{${envPattern}\\}`, "g"), "")
    .replace(new RegExp(`\\\\end\\{${envPattern}\\}`, "g"), "")
    .replace(/&/g, "")
    .replace(/```[a-zA-Z]*\n?/g, "")
    .replace(/```/g, "")
    .replace(/\\\(/g, "")
    .replace(/\\\)/g, "")
    .replace(/\\\[/g, "")
    .replace(/\\\]/g, "")
    .replace(/\$/g, "")
    .replace(/(?<!\\)\\(?:,|;|:|!|thinspace|medspace|thickspace|enspace)(?![a-zA-Z])/g, " ")
    .replace(/\\quad\b/g, " ")
    .replace(/\\qquad\b/g, " ")
    .replace(/\\(?:mathrm|text|operatorname)\{d\}/g, "d")
    .trim();

  return splitLatexStepsForPayload(value).join(" \\\\ ");
}

function stripMathDelimiters(line) {
  return String(line || "")
    .replace(/```[a-zA-Z]*\n?/g, "")
    .replace(/```/g, "")
    .replace(/\\\(/g, "")
    .replace(/\\\)/g, "")
    .replace(/\\\[/g, "")
    .replace(/\\\]/g, "")
    .replace(/\$/g, "")
    .replace(/(?<!\\)\\(?:,|;|:|!|thinspace|medspace|thickspace|enspace)(?![a-zA-Z])/g, " ")
    .replace(/\\quad\b/g, " ")
    .replace(/\\qquad\b/g, " ")
    .replace(/\\(?:mathrm|text|operatorname)\{d\}/g, "d")
    .trim();
}

function isStructuralLatexLine(line) {
  const text = String(line || "").trim();
  if (!text) return true;
  if (/^\\(begin|end)\{/.test(text)) return true;
  if (/^\\(left|right|bigl|bigr|Bigl|Bigr|big|Big)\s*[\\{}[\]().|]+$/.test(text)) return true;
  if (/^[{}[\]().|]+$/.test(text)) return true;
  const stripped = text
    .replace(/\\(left|right|bigl|bigr|Bigl|Bigr|big|Big)/g, "")
    .replace(/[{}[\]().|]/g, "")
    .trim();
  return stripped.length === 0;
}

function isNarrativeLeadInStep(line) {
  const text = String(line || "").trim();
  if (!text) return false;
  if (/[=<>]/.test(text)) return false;
  if (/^[=+\-*/]/.test(text)) return false;
  if (/\\sqrt|\\frac|\\cdot|\\times|\\div|\d/.test(text)) return false;
  if (/^In\s+(?:\\+(?:Delta|triangle)|triangle)\b/i.test(text)) return true;
  return /^(Using|By|From|Hence|Therefore|Thus|Now|Then|Since)\b/i.test(text) && /[,:;]$/.test(text);
}

function mergeNarrativeLeadInSteps(lines) {
  const merged = [];
  let pendingLeadIn = "";
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const text = String(line || "").trim();
    if (!text) return;
    if (pendingLeadIn) {
      merged.push(`${pendingLeadIn} ${text}`.replace(/\s+/g, " ").trim());
      pendingLeadIn = "";
      return;
    }
    if (isNarrativeLeadInStep(text)) {
      pendingLeadIn = text;
      return;
    }
    merged.push(text);
  });
  if (pendingLeadIn) merged.push(pendingLeadIn);
  return merged;
}

function splitLatexStepsForPayload(input) {
  const raw = String(input || "");
  return mergeNarrativeLeadInSteps(
    raw
      .split(/\s*\\\\\s*|\n+|\\quad\s*|(?<=\S)\s*\\\s+(?=[A-Za-z0-9_]+\s*=|=[^=])/)
      .map((line) => stripMathDelimiters(line))
      .filter(Boolean)
      .filter((line) => !isStructuralLatexLine(line))
  );
}

function getInkLaneMap(sourceLines, laneHasInk) {
  return [...new Set((Array.isArray(sourceLines) ? sourceLines : []).map((s) => Number(s.laneIndex)))]
    .filter((idx) => Number.isFinite(idx) && idx >= 0)
    .sort((a, b) => a - b)
    .filter((idx) => (typeof laneHasInk === "function" ? laneHasInk(idx) : true));
}

function buildMathLaneMapFromAttempt(attempt, fallbackLines, laneHasInk) {
  const liveLines = Array.isArray(attempt?.ocrLiveLines) ? attempt.ocrLiveLines : [];
  const map = liveLines
    .map((line) => ({
      lineOrder: Number(line?.lineOrder),
      contentKind: String(line?.contentKind || "").trim().toLowerCase(),
      latex: String(line?.latex || "").trim()
    }))
    .filter((line) => Number.isFinite(line.lineOrder) && line.lineOrder >= 0)
    .filter((line) => line.latex && !/\[unclear\]/i.test(line.latex))
    .filter((line) => {
      if (!line.contentKind) return true;
      return !["diagram", "blank", "unclear"].includes(line.contentKind);
    })
    .sort((a, b) => a.lineOrder - b.lineOrder)
    .map((line) => line.lineOrder);

  return map.length ? [...new Set(map)] : getInkLaneMap(fallbackLines, laneHasInk);
}

function getAttemptDiagramSummary(attempt) {
  return attempt?.slateDiagramSummary || attempt?.studentFigureSummary || null;
}

function getDiagramSummaryText(summary) {
  if (!summary) return "";
  if (typeof summary === "string") return summary.trim();
  if (summary.plainText) return String(summary.plainText).trim();
  const parts = [];
  const pushGroup = (label, values) => {
    if (!Array.isArray(values) || !values.length) return;
    parts.push(`${label}: ${values.slice(0, 6).join("; ")}`);
  };
  pushGroup("Relations", summary.relationSummary);
  pushGroup("Givens", summary.givenSummary);
  pushGroup("Dependencies", summary.dependencySummary);
  pushGroup("Entities", summary.entitySummary);
  return parts.join("\n").trim();
}

function getOcrLaneOrder(line, fallbackIndex = 0) {
  const explicit = Number(line?.lineOrder);
  if (Number.isFinite(explicit)) return explicit;
  const match = String(line?.lineId || "").match(/(?:lane|step)_(\d+)/i);
  if (match) {
    const fromId = Number(match[1]);
    if (Number.isFinite(fromId)) return fromId;
  }
  return fallbackIndex;
}

function normalizeOcrLaneRowsFromAttempt(attempt) {
  const liveLines = Array.isArray(attempt?.ocrLiveLines) ? attempt.ocrLiveLines : [];
  return liveLines
    .map((line, index) => {
      const latex = String(line?.latex || "").trim();
      const diagramSummaryText = getDiagramSummaryText(line?.diagramSummary || null);
      const rawKind = String(line?.contentKind || "").trim().toLowerCase();
      const contentKind = ["math", "diagram", "mixed", "blank", "unclear"].includes(rawKind)
        ? rawKind
        : diagramSummaryText
          ? (latex ? "mixed" : "diagram")
          : latex
            ? "math"
            : "blank";
      return {
        lineId: String(line?.lineId || `lane_${index}`),
        lineOrder: getOcrLaneOrder(line, index),
        contentKind,
        latex,
        diagramSummaryText,
        confidence: String(line?.confidence || "").trim().toLowerCase()
      };
    })
    .filter((row) => row.contentKind !== "blank")
    .filter((row) => row.latex || row.diagramSummaryText || row.contentKind === "unclear")
    .sort((a, b) => {
      if (a.lineOrder !== b.lineOrder) return a.lineOrder - b.lineOrder;
      return a.lineId.localeCompare(b.lineId);
    });
}

function getOcrLaneKindLabel(kind) {
  if (kind === "diagram") return "Diagram understood";
  if (kind === "mixed") return "Math + diagram";
  if (kind === "unclear") return "Unclear";
  return "Math";
}
function buildInitialLaneHeights(count = INITIAL_LANE_COUNT) {
  return Array.from({ length: Math.max(2, Number(count) || INITIAL_LANE_COUNT) }, () => DEFAULT_LANE_HEIGHT);
}

function getAutoLaneCount(isFullScreen = false) {
  if (typeof window === "undefined") return INITIAL_LANE_COUNT;
  const viewportHeight = Number(window.innerHeight) || 800;
  const chromeHeight = isFullScreen ? 120 : 220;
  const availableCanvasHeight = Math.max(280, viewportHeight - chromeHeight);
  const fitCount = Math.round(availableCanvasHeight / DEFAULT_LANE_HEIGHT);
  return Math.max(3, Math.min(fitCount, 8));
}

function getLaneBounds(heights) {
  let cursor = 0;
  return (Array.isArray(heights) ? heights : []).map((h, index) => {
    const height = Math.max(MIN_LANE_HEIGHT, Number(h) || DEFAULT_LANE_HEIGHT);
    const top = cursor;
    const bottom = top + height;
    cursor = bottom;
    return { index, top, bottom, height };
  });
}

function getLaneIndexByY(y, laneBounds) {
  const list = Array.isArray(laneBounds) ? laneBounds : [];
  if (!list.length) return 0;
  for (let i = 0; i < list.length; i += 1) {
    if (y >= list[i].top && y <= list[i].bottom) return i;
  }
  if (y < list[0].top) return 0;
  return list[list.length - 1].index;
}

function getLineIdForLane(laneIndex) {
  return `lane_${Number(laneIndex) || 0}`;
}

function getStrokeBounds(points, width = 0) {
  const list = Array.isArray(points) ? points : [];
  if (list.length < 2) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < list.length; i += 2) {
    const x = Number(list[i]);
    const y = Number(list[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  const pad = Math.max(0, Number(width) || 0) / 2;
  return {
    minX: minX - pad,
    minY: minY - pad,
    maxX: maxX + pad,
    maxY: maxY + pad
  };
}

function getStatusMeta(status) {
  if (status === "incorrect_but_right_direction") {
    return { label: "Fix", className: "status-incomplete" };
  }
  if (status === "wrong_direction") return { label: "Off path", className: "status-incorrect" };
  if (status === "dependent_on_error") return { label: "Blocked", className: "status-review" };
  if (status === "ahead_of_frontier") return { label: "Later", className: "status-unclear" };
  if (status === "superseded") return { label: "Replaced", className: "status-review" };
  if (status === "incorrect") return { label: "Incorrect", className: "status-incorrect" };
  if (status === "incomplete") return { label: "Incomplete", className: "status-incomplete" };
  if (status === "missing_step") return { label: "Missing", className: "status-missing" };
  if (status === "unclear") return { label: "Unclear", className: "status-unclear" };
  if (status === "correct") return { label: "Correct", className: "status-correct" };
  return { label: "Review", className: "status-review" };
}

function getTutorModeMeta(mode) {
  if (mode === "redirect") return { label: "Course correction", className: "mode-redirect" };
  if (mode === "diagnostic_probe") return { label: "Diagnostic", className: "mode-diagnostic_probe" };
  if (mode === "celebrate") return { label: "Complete", className: "mode-celebrate" };
  if (mode === "entry_nudge") return { label: "Start", className: "mode-entry_nudge" };
  if (mode === "info") return { label: "Info", className: "mode-info" };
  return { label: "Next hint", className: "mode-next_step_hint" };
}

function Practice({ apiBase, token, user, onPageBusyChange, isDemo, demoChapterId, isCustomQuestion, customQuestionId }) {
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const { chapterId: routeChapterId, questionId: routeQuestionId } = useParams();
  const isCustom = Boolean(isCustomQuestion || routeQuestionId || customQuestionId);
  const activeCustomQuestionId = customQuestionId || routeQuestionId;
  const chapterId = isCustom ? "custom" : (routeChapterId || demoChapterId);
  const location = useLocation();
  const navigate = useNavigate();
  const [customNav, setCustomNav] = useState(null);
  const [assignmentMeta, setAssignmentMeta] = useState(null);
  const [testTimeRemaining, setTestTimeRemaining] = useState(null);
  const [testTimeToStart, setTestTimeToStart] = useState(null);
  const [isTestSubmitted, setIsTestSubmitted] = useState(false);
  const [submittingTest, setSubmittingTest] = useState(false);
  const [isHomeworkSubmitted, setIsHomeworkSubmitted] = useState(false);
  const [submittingHomework, setSubmittingHomework] = useState(false);
  const [isQuestionSubmitted, setIsQuestionSubmitted] = useState(false);
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [examModal, setExamModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    progress: 0,
    progressLabel: "",
    detail: "",
    isLocked: false,
    confirmText: "OK",
    cancelText: "Cancel",
    onConfirm: null,
    onCancel: null
  });
  const [level, setLevel] = useState("LOT");
  const [strokeColor, setStrokeColor] = useState("#1d4ed8");
  const [strokeTool, setStrokeTool] = useState("pen");
  const [showPenMenu, setShowPenMenu] = useState(false);
  const [redoStack, setRedoStack] = useState([]);
  const [lines, setLines] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const slateRef = useRef(null);
  const canvasPaneRef = useRef(null);
  const stageRef = useRef(null);
  const activeStrokeCanvasRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 420 });
  const [question, setQuestion] = useState(null);
  const [frame, setFrame] = useState(null);
  const [selectedFrameOrder, setSelectedFrameOrder] = useState(null);
  const [currentFrameOrder, setCurrentFrameOrder] = useState(null);
  const [, setAllFrameOrders] = useState([]);
  const [nextFrameOrder, setNextFrameOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [analysisStatus, setAnalysisStatus] = useState("");
  const [analysisFeedback, setAnalysisFeedback] = useState("");
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisAlignment, setAnalysisAlignment] = useState([]);
  const [analysisFinalCorrect, setAnalysisFinalCorrect] = useState(null);
  const [analysisLaneMap, setAnalysisLaneMap] = useState([]);
  const [, setSlateDiagramSummary] = useState(null);
  const [ocrLaneRows, setOcrLaneRows] = useState([]);
  const [stepClusters, setStepClusters] = useState([]);
  const stepClustersRef = useRef([]);
  const previousMathClustersCountRef = useRef(0);
  const clusterDebounceTimersRef = useRef(new Map());
  const clusterFingerprintsRef = useRef({});

  // Photo submission states
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isPhotoSubmissionActive, setIsPhotoSubmissionActive] = useState(false);
  const [photoSubmissionPages, setPhotoSubmissionPages] = useState([]);
  const [isPhotoSubmitting, setIsPhotoSubmitting] = useState(false);
  const [photoProcessingPhase, setPhotoProcessingPhase] = useState("");
  const [detectedStepBoxes, setDetectedStepBoxes] = useState([]);

  const [paperStyle, setPaperStyle] = useState(() => {
    try {
      return localStorage.getItem("inksolver_slate_paper") || "white";
    } catch (_) {
      return "white";
    }
  });

  const handleSetPaperStyle = useCallback((style) => {
    setPaperStyle(style);
    try {
      localStorage.setItem("inksolver_slate_paper", style);
    } catch (_) {}
  }, []);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  const loadedQueryKeyRef = useRef("");

  // Cleanup is backend-owned: it only deletes the one onboarding attempt recorded on this user profile.
  useEffect(() => {
    return () => {
      if (isDemo) {
        const activeToken = tokenRef.current;
        if (!activeToken) return;
        fetch(`${apiBase}/api/attempts/onboarding-cleanup`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${activeToken}`
          },
          body: JSON.stringify({}),
          keepalive: true
        }).catch(err => console.error("Failed to cleanup onboarding attempt:", err));
      }
    };
  }, [isDemo, apiBase]);

  const [tutorHintData, setTutorHintData] = useState(null);
  const [tutorHintHistory, setTutorHintHistory] = useState([]);
  const [hintingIntent, setHintingIntent] = useState(null);
  const [tutorChatOpen, setTutorChatOpen] = useState(false);
  const [tutorLaneIndex, setTutorLaneIndex] = useState(0);
  const [selectedDiagStep, setSelectedDiagStep] = useState(null);
  const [ocrAttemptId, setOcrAttemptId] = useState("");
  const [ocrEditedLatex, setOcrEditedLatex] = useState("");
  const [ocrReady, setOcrReady] = useState(false);
  const [questionPanelOpen, setQuestionPanelOpen] = useState(false);
  const [figurePanelOpen, setFigurePanelOpen] = useState(true);
  const [workTab, setWorkTab] = useState("slate");
  const [showMathKeyboard, setShowMathKeyboard] = useState(false);
  const [laneHeights, setLaneHeights] = useState(() => buildInitialLaneHeights(getAutoLaneCount(false)));
  const [laneDragBoundary, setLaneDragBoundary] = useState(null);
  const [lastAttemptId, setLastAttemptId] = useState("");
  const [feedbackHelpful, setFeedbackHelpful] = useState("");
  const [feedbackReasons, setFeedbackReasons] = useState([]);
  const [feedbackStatus, setFeedbackStatus] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [slateFullScreen, setSlateFullScreen] = useState(false);
  const [isOcrSubmitting, setIsOcrSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isHinting, setIsHinting] = useState(false);
  const [retryAttemptId, setRetryAttemptId] = useState("");
  const [liveOcrStatus, setLiveOcrStatus] = useState("");
  const [studentFigureAssist, setStudentFigureAssist] = useState(null);
  const [draftFigureAssist, setDraftFigureAssist] = useState(null);
  const [collabInviteOpen, setCollabInviteOpen] = useState(false);
  const [collabInviteTarget, setCollabInviteTarget] = useState("");
  const [collabInviteUrl, setCollabInviteUrl] = useState("");
  const [collabInviteBusy, setCollabInviteBusy] = useState(false);
  const [collabInviteError, setCollabInviteError] = useState("");
  const [collabInviteLookupBusy, setCollabInviteLookupBusy] = useState(false);
  const [collabInviteLookupMode, setCollabInviteLookupMode] = useState("username");
  const [collabInviteLookupNote, setCollabInviteLookupNote] = useState("");
  const [collabInviteMatches, setCollabInviteMatches] = useState([]);
  const [collabInviteSelectedUser, setCollabInviteSelectedUser] = useState(null);
  const [collabInviteResolvedUser, setCollabInviteResolvedUser] = useState(null);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const {
    presentationProfile: slateProfile,
    runtimeSettings: slateProfileConfig
  } = useInkDeviceProfile({ apiBase, token });
  const requestIdRef = useRef(0);
  const lastQueryRef = useRef("");
  const lastFetchAtRef = useRef(0);
  const inFlightRef = useRef(false);
  const lastAbortCandidateRef = useRef({ queryKey: "", startedAt: 0 });
  const linesRef = useRef([]);
  const ocrEditedLatexRef = useRef("");
  const mathEditorUserEditedRef = useRef(false);
  const laneHeightsRef = useRef(laneHeights);
  const ocrAttemptIdRef = useRef("");
  const liveOcrTimerRef = useRef(null);
  const liveOcrInFlightRef = useRef(false);
  const liveOcrFrozenRef = useRef(false);
  const pendingLineUpdatesRef = useRef(new Map());
  const pendingLineOrderRef = useRef([]);
  const laneRevisionRef = useRef({});
  const laneAckRevisionRef = useRef({});
  const laneInkStateRef = useRef({});
  const laneDragRef = useRef(null);
  const activeLaneRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const pointerOwnershipRef = useRef(createInkPointerOwnership(slateProfileConfig));
  const drawPendingPointsRef = useRef([]);
  const drawRafRef = useRef(null);
  const syncStageSizeRafRef = useRef(null);
  const activeStrokePointsRef = useRef([]);
  const activeStrokeMetaRef = useRef(null);
  const activeStrokePointProcessorRef = useRef(null);
  const renderCanvasRef = useRef(null);
  const renderCropCanvasRef = useRef(null);
  const renderFullCanvasRef = useRef(null);
  const renderFullCropCanvasRef = useRef(null);
  const toolbarMenuRef = useRef(null);
  const diagnosticRailRef = useRef(null);
  const lineConfidenceRef = useRef({});
  const liveSessionRef = useRef(0);
  const activePointerTypeRef = useRef("");
  const isDrawingRef = useRef(false);
  const deferredLiveOcrStatusRef = useRef(null);
  const deferredLiveOcrEditorRef = useRef(null);
  const draftFigureAssistRef = useRef(null);
  const studentFigureAssistRef = useRef(null);
  const figureAcceptInFlightRef = useRef(null);
  const lastAcceptedFigureDraftRef = useRef(null);
  const tutorHintEntryIdRef = useRef(0);
  const collabInviteLookupTimerRef = useRef(null);
  const collabInviteLookupRequestRef = useRef(0);
  const getNativePointerId = useCallback((nativeEvent) => {
    const pointerId = Number(nativeEvent?.pointerId);
    return Number.isFinite(pointerId) ? pointerId : null;
  }, []);

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("practice", loading, "Loading practice...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("practice", false);
      }
    };
  }, [loading, onPageBusyChange]);
  const updateLiveOcrStatus = useCallback((nextStatus, options = {}) => {
    const next = String(nextStatus || "");
    if (!options?.force && isDrawingRef.current) {
      deferredLiveOcrStatusRef.current = next;
      return;
    }
    deferredLiveOcrStatusRef.current = null;
    setLiveOcrStatus(next);
  }, []);
  const clearTutorHint = useCallback(() => {
    setTutorHintData(null);
  }, []);
  const rememberTutorHint = useCallback((status, tutor, options = {}) => {
    tutorHintEntryIdRef.current += 1;
    const intent = typeof options === "string" ? options : (options?.intent || tutor?.intent || "next_step");
    const entry = {
      id: tutorHintEntryIdRef.current,
      status: String(status || "Hint"),
      createdAt: Date.now(),
      tutor: tutor ? { ...tutor, intent } : null,
      intent
    };
    setTutorHintHistory((prev) => [...prev, entry].slice(-12));
  }, []);
  const resetTransientActionState = useCallback(() => {
    setLiveOcrFrozen(false);
    setIsOcrSubmitting(false);
    setIsAnalyzing(false);
    setIsHinting(false);
    clearTutorHint();
  }, [clearTutorHint]);
  const updateOcrEditedLatex = useCallback((nextValue) => {
    const next = String(nextValue || "");
    clearTutorHint();
    ocrEditedLatexRef.current = next;
    setOcrEditedLatex(next);
  }, [clearTutorHint]);
  const handleMathLiveEditorChange = useCallback((nextValue) => {
    mathEditorUserEditedRef.current = true;
    updateOcrEditedLatex(nextValue);
  }, [updateOcrEditedLatex]);
  const applyLiveOcrEditorUpdate = useCallback((nextLatex, nextReady) => {
    const payload = {
      latex: String(nextLatex || ""),
      ready: Boolean(nextReady)
    };
    if (isDrawingRef.current) {
      deferredLiveOcrEditorRef.current = payload;
      return;
    }
    deferredLiveOcrEditorRef.current = null;
    updateOcrEditedLatex(payload.latex);
    setOcrReady(payload.ready);
  }, [updateOcrEditedLatex]);
  const collabInviteQuery = String(collabInviteTarget || "").trim();
  const collabInviteSearchMode = useMemo(() => getCollabInviteMode(collabInviteQuery), [collabInviteQuery]);
  const collabInviteMatchedUser = collabInviteResolvedUser || collabInviteSelectedUser;
  const collabInviteHasMatchedUser = Boolean(collabInviteMatchedUser?.uid);
  const collabInviteShowShareRail = Boolean(
    collabInviteQuery || collabInviteSelectedUser || collabInviteResolvedUser || collabInviteUrl
  );
  pointerOwnershipRef.current.setSettings(slateProfileConfig);
  const collabInviteShareReady = Boolean(collabInviteUrl);
  const collabInviteShareBody = useMemo(() => {
    if (!collabInviteUrl) return "";
    const lines = [
      "Join me on Ink Together for this question.",
      collabInviteUrl
    ];
    return lines.join("\n\n");
  }, [collabInviteUrl]);
  const collabInviteMailToHref = useMemo(() => {
    if (!collabInviteUrl) return "";
    const emailTarget = String(
      collabInviteMatchedUser?.email ||
      (!collabInviteHasMatchedUser && collabInviteSearchMode === "email" ? collabInviteQuery : "")
    ).trim();
    const params = new URLSearchParams({
      subject: "Join my Ink Together room",
      body: collabInviteShareBody
    });
    return `mailto:${emailTarget}?${params.toString()}`;
  }, [
    collabInviteHasMatchedUser,
    collabInviteMatchedUser?.email,
    collabInviteQuery,
    collabInviteSearchMode,
    collabInviteShareBody,
    collabInviteUrl
  ]);
  const collabInviteWhatsAppText = useMemo(
    () => encodeURIComponent(collabInviteShareBody),
    [collabInviteShareBody]
  );
  const collabInviteWhatsAppHref = collabInviteUrl ? `whatsapp://send?text=${collabInviteWhatsAppText}` : "";
  const collabInviteWhatsAppWebHref = collabInviteUrl ? `https://wa.me/?text=${collabInviteWhatsAppText}` : "";

  useEffect(() => {
    draftFigureAssistRef.current = draftFigureAssist;
  }, [draftFigureAssist]);

  useEffect(() => {
    studentFigureAssistRef.current = studentFigureAssist;
  }, [studentFigureAssist]);

  useEffect(() => {
    return () => {
      if (collabInviteLookupTimerRef.current) {
        clearTimeout(collabInviteLookupTimerRef.current);
        collabInviteLookupTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!collabInviteOpen) return undefined;
    if (collabInviteLookupTimerRef.current) {
      clearTimeout(collabInviteLookupTimerRef.current);
      collabInviteLookupTimerRef.current = null;
    }

    if (!collabInviteQuery) {
      setCollabInviteLookupBusy(false);
      setCollabInviteLookupMode(collabInviteSearchMode);
      setCollabInviteLookupNote("");
      setCollabInviteMatches([]);
      return undefined;
    }

    if (collabInviteSearchMode === "username" && collabInviteQuery.length < 2) {
      setCollabInviteLookupBusy(false);
      setCollabInviteLookupMode("username");
      setCollabInviteLookupNote("");
      setCollabInviteMatches([]);
      return undefined;
    }

    const requestId = collabInviteLookupRequestRef.current + 1;
    collabInviteLookupRequestRef.current = requestId;
    setCollabInviteLookupBusy(true);
    setCollabInviteLookupMode(collabInviteSearchMode);
    setCollabInviteLookupNote("");

    collabInviteLookupTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `${apiBase}/api/collaboration-sessions/users/search?q=${encodeURIComponent(collabInviteQuery)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Could not look up students.");
        }

        const payload = await response.json().catch(() => ({}));
        if (collabInviteLookupRequestRef.current !== requestId) return;

        const matches = Array.isArray(payload?.matches) ? payload.matches : [];
        if (collabInviteSearchMode === "email") {
          const emailMatch = payload?.exactMatch ? matches[0] || null : null;
          setCollabInviteSelectedUser(emailMatch);
          setCollabInviteMatches(emailMatch ? [] : matches);
          setCollabInviteLookupNote(
            emailMatch
              ? "Existing student found. They will receive an in-app notification."
              : "No existing student found for this email yet. You can still share the room link manually."
          );
        } else {
          setCollabInviteMatches(matches);
          if (!matches.length) {
            setCollabInviteLookupNote("No usernames matched yet.");
          }
        }
      } catch (err) {
        if (collabInviteLookupRequestRef.current !== requestId) return;
        setCollabInviteLookupNote(err.message || "Could not look up students.");
        setCollabInviteMatches([]);
      } finally {
        if (collabInviteLookupRequestRef.current === requestId) {
          setCollabInviteLookupBusy(false);
        }
      }
    }, collabInviteSearchMode === "email" ? 240 : 180);

    return () => {
      if (collabInviteLookupTimerRef.current) {
        clearTimeout(collabInviteLookupTimerRef.current);
        collabInviteLookupTimerRef.current = null;
      }
    };
  }, [apiBase, collabInviteOpen, collabInviteQuery, collabInviteSearchMode, token]);
  const setSlatePointerLock = useCallback((locked) => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("slate-pointer-active", !!locked);
    document.body.classList.toggle("slate-pointer-active", !!locked);
    if (locked) {
      const activeEl = document.activeElement;
      if (
        activeEl &&
        typeof activeEl.blur === "function" &&
        activeEl !== document.body
      ) {
        try {
          activeEl.blur();
        } catch (err) {
          // Ignore blur failures on restrictive browsers.
        }
      }
    }
    if (!locked || typeof window === "undefined" || typeof window.getSelection !== "function") return;
    try {
      window.getSelection()?.removeAllRanges?.();
    } catch (err) {
      // Ignore selection clearing issues on restrictive browsers.
    }
  }, []);
  const tutorStepDiagnostics = useMemo(() => {
    const map = {};
    (Array.isArray(tutorHintData?.stepLedger) ? tutorHintData.stepLedger : []).forEach((item) => {
      const stepNo = Number(item?.studentStep);
      if (!Number.isFinite(stepNo) || stepNo <= 0) return;
      map[stepNo] = {
        status: item.status || "review",
        reason: item.reason || ""
      };
    });
    return map;
  }, [tutorHintData]);
  const showSlateDiagnostics =
    (analysisStatus === "Analysis complete" && analysisAlignment.length > 0) ||
    Object.keys(tutorStepDiagnostics).length > 0;
  const laneBounds = useMemo(() => getLaneBounds(laneHeights), [laneHeights]);
  const stageHeight = useMemo(() => {
    const total = laneBounds.length ? laneBounds[laneBounds.length - 1].bottom : 0;
    return Math.max(280, Math.ceil(total));
  }, [laneBounds]);

  const stageRenderWidth = useMemo(() => {
    const baseWidth = Math.max(MIN_STAGE_WIDTH, Math.floor(Number(stageSize.width) || MIN_STAGE_WIDTH));
    const diagnosticPad = showSlateDiagnostics ? DIAGNOSTIC_RAIL_WIDTH + 16 : 0;
    const rightPad = STAGE_CONTENT_RIGHT_PAD + diagnosticPad;
    let maxX = 0;
    for (const stroke of Array.isArray(lines) ? lines : []) {
      if (stroke?.tool === "eraser") continue;
      const bounds = getStrokeBounds(stroke?.points, stroke?.width);
      if (!bounds) continue;
      if (bounds.maxX > maxX) maxX = bounds.maxX;
    }
    return Math.max(baseWidth + diagnosticPad, Math.ceil(maxX + rightPad));
  }, [lines, showSlateDiagnostics, stageSize.width]);

  const getEffectiveStageWidth = useCallback(() => stageRenderWidth, [stageRenderWidth]);

  const syncStageSize = useCallback(() => {
    if (!slateRef.current) return;
    const style = window.getComputedStyle(slateRef.current);
    const paddingLeft = parseFloat(style.paddingLeft || "0");
    const paddingRight = parseFloat(style.paddingRight || "0");
    const contentWidth = Math.max(
      MIN_STAGE_WIDTH,
      Math.floor(slateRef.current.clientWidth - paddingLeft - paddingRight)
    );
    setStageSize((prev) =>
      prev.width === contentWidth && prev.height === stageHeight
        ? prev
        : { width: contentWidth, height: stageHeight }
    );
  }, [stageHeight]);

  const scheduleSyncStageSize = useCallback(() => {
    if (typeof window === "undefined") {
      syncStageSize();
      return;
    }
    if (syncStageSizeRafRef.current) {
      window.cancelAnimationFrame(syncStageSizeRafRef.current);
    }
    syncStageSizeRafRef.current = window.requestAnimationFrame(() => {
      syncStageSizeRafRef.current = null;
      syncStageSize();
    });
  }, [syncStageSize]);

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
    if (!isDrawing && deferredLiveOcrStatusRef.current !== null) {
      setLiveOcrStatus(deferredLiveOcrStatusRef.current);
      deferredLiveOcrStatusRef.current = null;
    }
    if (!isDrawing && deferredLiveOcrEditorRef.current) {
      updateOcrEditedLatex(deferredLiveOcrEditorRef.current.latex);
      setOcrReady(Boolean(deferredLiveOcrEditorRef.current.ready));
      deferredLiveOcrEditorRef.current = null;
    }
  }, [isDrawing, updateOcrEditedLatex]);

  useEffect(() => {
    laneHeightsRef.current = laneHeights;
  }, [laneHeights]);

  useEffect(() => {
    ocrAttemptIdRef.current = ocrAttemptId;
  }, [ocrAttemptId]);

  function resetLiveTrackingState() {
    if (liveOcrTimerRef.current) {
      clearTimeout(liveOcrTimerRef.current);
      liveOcrTimerRef.current = null;
    }
    liveOcrFrozenRef.current = false;
    liveOcrInFlightRef.current = false;
    setIsOcrSubmitting(false);
    setIsAnalyzing(false);
    setIsHinting(false);
    pendingLineUpdatesRef.current = new Map();
    pendingLineOrderRef.current = [];
    laneRevisionRef.current = {};
    laneAckRevisionRef.current = {};
    laneInkStateRef.current = {};
    lineConfidenceRef.current = {};
    activeLaneRef.current = null;
    setTutorLaneIndex(0);
    setTutorHintHistory([]);
    liveSessionRef.current += 1;
    figureAcceptInFlightRef.current = null;
    lastAcceptedFigureDraftRef.current = null;
    clusterDebounceTimersRef.current.forEach((t) => clearTimeout(t));
    clusterDebounceTimersRef.current.clear();
    clusterFingerprintsRef.current = {};
    previousMathClustersCountRef.current = 0;
    setStepClusters([]);
    stepClustersRef.current = [];
  }

  useEffect(() => {
    const host = canvasPaneRef.current || stageRef.current?.container?.();
    if (!host || workTab !== "slate") return undefined;
    const listenerOptions = { passive: false, capture: true };
    const preventNativeGesture = (event) => {
      if (!isDrawingRef.current && !laneDragRef.current) return;
      if (event.cancelable) event.preventDefault();
    };
    const preventSelection = (event) => {
      if (event.cancelable) event.preventDefault();
    };

    [
      "touchstart",
      "touchmove",
      "touchend",
      "gesturestart",
      "gesturechange",
      "gestureend"
    ].forEach((eventName) => host.addEventListener(eventName, preventNativeGesture, listenerOptions));
    host.addEventListener("selectstart", preventSelection, listenerOptions);
    host.addEventListener("dragstart", preventSelection, listenerOptions);

    return () => {
      [
        "touchstart",
        "touchmove",
        "touchend",
        "gesturestart",
        "gesturechange",
        "gestureend"
      ].forEach((eventName) => host.removeEventListener(eventName, preventNativeGesture, listenerOptions));
      host.removeEventListener("selectstart", preventSelection, listenerOptions);
      host.removeEventListener("dragstart", preventSelection, listenerOptions);
    };
  }, [stageSize.width, stageSize.height, slateFullScreen, workTab]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const difficultyParam = String(params.get("difficulty") || "").toUpperCase();
    const frameOrderParam = params.get("frameOrder");
    const retryAttemptParam = String(params.get("retryAttemptId") || "").trim();

    if (difficultyParam && levelOrder.includes(difficultyParam)) {
      setLevel((prev) => (prev === difficultyParam ? prev : difficultyParam));
    }

    if (frameOrderParam !== null && frameOrderParam !== "") {
      const parsed = Number(frameOrderParam);
      if (Number.isFinite(parsed)) {
        setSelectedFrameOrder((prev) => (Number(prev) === parsed ? prev : parsed));
      }
    }

    setRetryAttemptId((prev) => (prev === retryAttemptParam ? prev : retryAttemptParam));
  }, [location.search]);

  useEffect(() => {
    scheduleSyncStageSize();
    window.addEventListener("resize", scheduleSyncStageSize);

    let observer = null;
    if (typeof ResizeObserver !== "undefined" && slateRef.current) {
      observer = new ResizeObserver(() => scheduleSyncStageSize());
      observer.observe(slateRef.current);
    }

    // Fallback syncs for delayed layout shifts (e.g., fonts loading or animations finishing)
    const t1 = setTimeout(scheduleSyncStageSize, 150);
    const t2 = setTimeout(scheduleSyncStageSize, 600);
    const t3 = setTimeout(scheduleSyncStageSize, 1500);

    return () => {
      window.removeEventListener("resize", scheduleSyncStageSize);
      if (observer) observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (syncStageSizeRafRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(syncStageSizeRafRef.current);
        syncStageSizeRafRef.current = null;
      }
    };
  }, [scheduleSyncStageSize, workTab]);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") {
        setSlateFullScreen(false);
        setShowAttachmentModal(false);
      }
    }

    if (slateFullScreen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [slateFullScreen]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!showPenMenu) return;
      const host = toolbarMenuRef.current;
      if (host && !host.contains(event.target)) {
        setShowPenMenu(false);
      }
    }
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [showPenMenu]);

  useEffect(() => {
    return () => {
      if (liveOcrTimerRef.current) {
        clearTimeout(liveOcrTimerRef.current);
        liveOcrTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    scheduleSyncStageSize();
  }, [slateFullScreen, stageHeight, scheduleSyncStageSize]);

  useEffect(() => {
    const growRowsToViewport = () => {
      const targetCount = getAutoLaneCount(slateFullScreen);
      setLaneHeights((prev) => {
        if (prev.length >= targetCount) return prev;
        return [...prev, ...Array.from({ length: targetCount - prev.length }, () => DEFAULT_LANE_HEIGHT)];
      });
    };
    growRowsToViewport();
    if (!slateFullScreen) return undefined;
    window.addEventListener("resize", growRowsToViewport);
    return () => window.removeEventListener("resize", growRowsToViewport);
  }, [slateFullScreen]);

  const hasToken = Boolean(token);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function loadQuestion() {
      if (!tokenRef.current) return;
      const queryKey = isCustom && activeCustomQuestionId
        ? `custom:${activeCustomQuestionId}`
        : `${chapterId}|${level}|${selectedFrameOrder ?? ""}|${retryAttemptId ?? ""}`;
      if (loadedQueryKeyRef.current === queryKey) return;
      if (inFlightRef.current && lastQueryRef.current === queryKey) return;
      inFlightRef.current = true;
      lastQueryRef.current = queryKey;
      lastFetchAtRef.current = Date.now();
      lastAbortCandidateRef.current = { queryKey, startedAt: lastFetchAtRef.current };
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError("");
      setAnalysisStatus("");
      setAnalysisFeedback("");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setAnalysisLaneMap([]);
      setSelectedDiagStep(null);
      setOcrAttemptId("");
      updateOcrEditedLatex("");
      setOcrReady(false);
      setShowClearConfirm(false);
      setWorkTab("slate");
      setShowMathKeyboard(false);
      updateLiveOcrStatus("", { force: true });
      resetTransientActionState();
      setStrokeTool("pen");
      setStudentFigureAssist(null);
      setDraftFigureAssist(null);
      resetLiveTrackingState();

      try {
        let payload;
        if (isCustom && activeCustomQuestionId) {
          const searchParams = new URLSearchParams(location.search);
          const activeSetId = searchParams.get("setId");
          const customUrl = `${apiBase}/api/questions/custom/${activeCustomQuestionId}${activeSetId ? `?setId=${encodeURIComponent(activeSetId)}` : ""}`;
          const response = await fetch(customUrl, {
            headers: {
              Authorization: `Bearer ${tokenRef.current || token}`
            },
            signal: controller.signal
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || "Failed to load custom question");
          }
          payload = await response.json();
        } else {
          const query = new URLSearchParams({
            chapterId,
            difficulty: level
          });
          if (selectedFrameOrder !== null) {
            query.set("frameOrder", String(selectedFrameOrder));
          }
          if (retryAttemptId) {
            query.set("retryAttemptId", retryAttemptId);
          }

          const response = await fetch(`${apiBase}/api/question?${query.toString()}`, {
            headers: {
              Authorization: `Bearer ${tokenRef.current || token}`
            },
            signal: controller.signal
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || "Failed to load question");
          }

          payload = await response.json();
        }

        if (isMounted && requestId === requestIdRef.current) {
          setQuestion(payload.question);
          setFrame(payload.frame);
          setAllFrameOrders(payload?.navigation?.allFrameOrders || []);
          setNextFrameOrder(payload?.navigation?.nextFrameOrder ?? null);
          setCurrentFrameOrder(payload?.navigation?.frameOrder ?? null);
          setCustomNav(payload?.navigation || null);
          const meta = payload?.assignmentMeta || payload?.navigation?.assignmentMeta || null;
          setAssignmentMeta(meta);
          const existingAttempt = payload?.existingAttempt || null;
          const isQSub = Boolean(existingAttempt?.isSubmitted);
          const isSetSub = Boolean(meta?.isAssignmentSubmitted || existingAttempt?.isAssignmentSubmitted || (isTestMode && meta?.isSubmitted));
          setIsQuestionSubmitted(isQSub);
          setIsHomeworkSubmitted(isSetSub);
          setIsTestSubmitted(Boolean(isTestMode && (meta?.isSubmitted || existingAttempt?.isSubmitted)));

          // Restore draft or existing attempt if available
          let restoredLines = [];
          let restoredLaneHeights = null;
          let restoredLatex = "";

          // Check local draft first for instant zero-latency switch
          if (isCustom && activeCustomQuestionId) {
            try {
              const draftKey = `inksolver_slate_draft_${userRef.current?.uid || "anon"}_${activeCustomQuestionId}`;
              const draftRaw = localStorage.getItem(draftKey);
              if (draftRaw) {
                const draft = JSON.parse(draftRaw);
                if (Array.isArray(draft.lines) && draft.lines.length > 0) {
                  restoredLines = draft.lines;
                  restoredLaneHeights = Array.isArray(draft.laneHeights) ? draft.laneHeights : null;
                  restoredLatex = draft.ocrEditedLatex || "";
                }
              }
            } catch (e) {
              console.warn("Could not parse draft from localStorage", e);
            }
          }

          // If no local draft strokes, check backend existingAttempt
          if (restoredLines.length === 0 && existingAttempt) {
            if (Array.isArray(existingAttempt.slateLines) && existingAttempt.slateLines.length > 0) {
              restoredLines = existingAttempt.slateLines;
            } else if (Array.isArray(existingAttempt.slateMeta?.lines) && existingAttempt.slateMeta.lines.length > 0) {
              restoredLines = existingAttempt.slateMeta.lines;
            }
            if (Array.isArray(existingAttempt.slateMeta?.laneHeights) && existingAttempt.slateMeta.laneHeights.length > 0) {
              restoredLaneHeights = existingAttempt.slateMeta.laneHeights;
            }
            if (existingAttempt.ocrEditedLatex) {
              restoredLatex = existingAttempt.ocrEditedLatex;
            }
          }

          setLines(restoredLines);
          linesRef.current = restoredLines;
          if (restoredLaneHeights && restoredLaneHeights.length > 0) {
            setLaneHeights(restoredLaneHeights);
            laneHeightsRef.current = restoredLaneHeights;
          } else {
            const initialHeights = buildInitialLaneHeights(getAutoLaneCount(false));
            setLaneHeights(initialHeights);
            laneHeightsRef.current = initialHeights;
          }
          updateOcrEditedLatex(restoredLatex);
          setOcrReady(Boolean(restoredLatex && String(restoredLatex).trim()));

          if (existingAttempt?._id) {
            setLastAttemptId(existingAttempt._id);
            ocrAttemptIdRef.current = existingAttempt._id;
          } else {
            setLastAttemptId("");
            ocrAttemptIdRef.current = "";
          }

          setLaneDragBoundary(null);
          resetLiveTrackingState();
          if (restoredLines.length > 0 && typeof syncInkClusters === "function") {
            try {
              syncInkClusters();
            } catch (_) {}
          }
          loadedQueryKeyRef.current = queryKey;
        }
      } catch (err) {
        if (isMounted && err.name !== "AbortError") {
          setError(err.message || "Failed to load question");
        }
      } finally {
        if (isMounted && requestId === requestIdRef.current) {
          setLoading(false);
        }
        inFlightRef.current = false;
      }

    }

    loadQuestion();

    return () => {
      isMounted = false;
      const { queryKey, startedAt } = lastAbortCandidateRef.current || {};
      const expectedKey = isCustom && activeCustomQuestionId
        ? `custom:${activeCustomQuestionId}`
        : `${chapterId}|${level}|${selectedFrameOrder ?? ""}|${retryAttemptId ?? ""}`;
      const isStrictModeDoubleRun =
        queryKey === expectedKey &&
        Date.now() - startedAt < 50;
      if (!isStrictModeDoubleRun) {
        controller.abort();
      }
      inFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, chapterId, level, hasToken, selectedFrameOrder, retryAttemptId, resetTransientActionState, updateLiveOcrStatus, updateOcrEditedLatex, isDemo, isCustom, activeCustomQuestionId]);

  // ── Test Mode & Sacrosanct Deadline Countdown ──
  useEffect(() => {
    if (!assignmentMeta || assignmentMeta.assignmentType !== "test") {
      setTestTimeRemaining(null);
      setTestTimeToStart(null);
      return;
    }

    const calcTiming = () => {
      const now = Date.now();
      if (assignmentMeta.startTime) {
        const startDiff = Math.floor((new Date(assignmentMeta.startTime).getTime() - now) / 1000);
        setTestTimeToStart(Math.max(0, startDiff));
      } else {
        setTestTimeToStart(0);
      }

      if (assignmentMeta.dueTimestamp) {
        const remDiff = Math.floor((new Date(assignmentMeta.dueTimestamp).getTime() - now) / 1000);
        setTestTimeRemaining(Math.max(0, remDiff));
      } else {
        setTestTimeRemaining(null);
      }
    };

    calcTiming();

    const timer = setInterval(() => {
      calcTiming();
    }, 1000);

    return () => clearInterval(timer);
  }, [assignmentMeta]);

  const isTestMode = assignmentMeta?.assignmentType === "test";
  const isHomework = Boolean(assignmentMeta?.assignmentType === "homework" || (isCustom && customNav?.setId && !isTestMode));
  const isExamNotStarted = isTestMode && testTimeToStart !== null && testTimeToStart > 0;
  const isExamLocked = isTestMode && (isTestSubmitted || Boolean(assignmentMeta?.isLocked) || testTimeRemaining === 0);

  const formatTestGoalLabel = (goal) => {
    switch (goal) {
      case "ib_dp": return "IB DP Math";
      case "cambridge_igcse": return "Cambridge IGCSE";
      case "cbse": return "CBSE Board";
      case "sat": return "Digital SAT";
      case "jee": return "JEE Adv/Main";
      default: return "Curriculum Exam";
    }
  };

  const formatTimeLeft = (seconds) => {
    if (seconds === null || seconds === undefined) return "";
    if (seconds <= 0) return "00:00 (Time Expired)";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins.toString().padStart(2, "0")}m ${secs.toString().padStart(2, "0")}s`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const draftSyncTimerRef = useRef(null);

  const getDraftStorageKey = useCallback((qId) => {
    const uid = userRef.current?.uid || "anon";
    return `inksolver_slate_draft_${uid}_${qId}`;
  }, []);

  const saveSlateDraft = useCallback((questionIdToSave, immediate = false, overrides = {}) => {
    const targetQId = questionIdToSave || activeCustomQuestionId;
    if (!targetQId || !isCustom) return;

    const currentLines = overrides.lines !== undefined ? overrides.lines : (linesRef.current || lines || []);
    const currentLaneHeights = overrides.laneHeights !== undefined ? overrides.laneHeights : (laneHeightsRef.current || laneHeights || []);
    const currentLatex = overrides.ocrEditedLatex !== undefined ? overrides.ocrEditedLatex : (ocrEditedLatexRef.current || ocrEditedLatex || "");
    const storageKey = getDraftStorageKey(targetQId);

    // 1. Immediately cache in localStorage for instant zero-latency retrieval across question switches
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        lines: currentLines,
        laneHeights: currentLaneHeights,
        ocrEditedLatex: currentLatex,
        updatedAt: Date.now()
      }));
    } catch (e) {
      console.warn("Failed to write slate draft to localStorage:", e);
    }

    // 2. Background sync to MongoDB draft endpoint
    const executeSync = async () => {
      try {
        const authToken = tokenRef.current || token;
        if (!authToken) return;
        await fetch(`${apiBase}/api/attempts/save-slate-draft`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            questionId: targetQId,
            setId: assignmentMeta?.setId || customNav?.setId || null,
            isTestMode: isTestMode,
            lines: currentLines,
            laneHeights: currentLaneHeights,
            stageSize: stageSize,
            ocrEditedLatex: currentLatex,
            ocrEditedSteps: currentLatex
              ? currentLatex.split(/\n|\\\\/).map((s) => s.trim()).filter(Boolean)
              : []
          })
        });
      } catch (err) {
        console.warn("Background slate draft sync failed:", err);
      }
    };

    if (draftSyncTimerRef.current) {
      clearTimeout(draftSyncTimerRef.current);
      draftSyncTimerRef.current = null;
    }

    if (immediate) {
      executeSync();
    } else {
      draftSyncTimerRef.current = setTimeout(executeSync, 1000);
    }
  }, [activeCustomQuestionId, isCustom, lines, laneHeights, ocrEditedLatex, isTestMode, stageSize, assignmentMeta?.setId, customNav?.setId, apiBase, token, getDraftStorageKey]);

  const handleNavigateQuestion = useCallback((targetQuestionId) => {
    if (!targetQuestionId || loading || isAnalyzing) return;

    // Immediately save current question's slate draft locally & to MongoDB
    if (isCustom && activeCustomQuestionId) {
      saveSlateDraft(activeCustomQuestionId, true);
    }

    const setParam = customNav?.setId ? `?setId=${encodeURIComponent(customNav.setId)}` : "";
    navigate(`/practice/custom/${targetQuestionId}${setParam}`, {
      state: location.state
    });
  }, [loading, isAnalyzing, isCustom, activeCustomQuestionId, customNav?.setId, navigate, saveSlateDraft, location.state]);

  const handleSubmitTest = () => {
    setExamModal({
      isOpen: true,
      type: "confirm",
      title: "Submit Exam?",
      message: "Are you sure you want to submit your exam now? Once submitted, your solutions are locked and cannot be modified.",
      confirmText: "Submit Exam",
      cancelText: "Keep Working",
      isLocked: false,
      onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
      onConfirm: async () => {
        setSubmittingTest(true);
        setExamModal({
          isOpen: true,
          type: "progress",
          title: "Submitting Exam...",
          message: "Finalizing your handwriting and locking solutions for tutor grading...",
          progress: 50,
          progressLabel: "Submitting...",
          detail: "Saving handwritten work and OCR latex...",
          isLocked: true,
          confirmText: "OK",
          cancelText: "Cancel",
          onConfirm: null,
          onCancel: null
        });

        try {
          updateLiveOcrStatus("Processing final handwriting before exam submission...");
          try {
            await finalizeLiveOcrBeforeAnalyze();
          } catch (ocrErr) {
            console.warn("Live OCR finalization prior to exam submit warning:", ocrErr);
          }

          if (isCustom && activeCustomQuestionId) {
            saveSlateDraft(activeCustomQuestionId, true);
          }

          const activeSetId = assignmentMeta?.setId || customNav?.setId;
          const latexToSubmit = ocrEditedLatexRef.current || ocrEditedLatex || "";
          const stepStringsToSubmit = latexToSubmit
            ? latexToSubmit.split(/\n|\\\\/).map((s) => s.trim()).filter(Boolean)
            : [];

          const res = await fetch(`${apiBase}/api/attempts/submit-test`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenRef.current || token}`
            },
            body: JSON.stringify({
              setId: activeSetId,
              currentQuestionId: activeCustomQuestionId,
              lines: linesRef.current || lines || [],
              laneHeights: laneHeightsRef.current || laneHeights || [],
              stageSize: stageSize,
              ocrEditedLatex: latexToSubmit,
              ocrEditedSteps: stepStringsToSubmit
            })
          });

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || "Failed to submit test");
          }

          const data = await res.json();
          setIsTestSubmitted(true);
          setExamModal({
            isOpen: true,
            type: "success",
            title: "Exam Submitted!",
            message: `Exam submitted successfully (${data.submittedCount || 1} question${data.submittedCount === 1 ? "" : "s"} submitted). Your solution is now locked and awaiting tutor grading.`,
            progress: 100,
            progressLabel: "100%",
            detail: "Solutions secured for teacher evaluation.",
            isLocked: false,
            confirmText: "Back to Assignments",
            cancelText: "Stay on Slate",
            onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
            onConfirm: () => {
              setExamModal((prev) => ({ ...prev, isOpen: false }));
              navigate(location?.state?.backTo || "/my-questions?tab=homework");
            }
          });
        } catch (err) {
          setExamModal({
            isOpen: true,
            type: "error",
            title: "Submission Failed",
            message: err.message || "Failed to submit test",
            isLocked: false,
            confirmText: "Close",
            onConfirm: () => setExamModal((prev) => ({ ...prev, isOpen: false }))
          });
        } finally {
          setSubmittingTest(false);
        }
      }
    });
  };



  function queueLiveLineOcr(delay = LIVE_OCR_FLUSH_DELAY_MS, options = {}) {
    if (liveOcrFrozenRef.current && !options?.allowWhileFrozen) return;
    if (liveOcrTimerRef.current) clearTimeout(liveOcrTimerRef.current);
    liveOcrTimerRef.current = setTimeout(() => {
      liveOcrTimerRef.current = null;
      flushLiveLineOcr(options);
    }, Math.max(LIVE_OCR_MIN_DELAY_MS, Number(delay) || LIVE_OCR_FLUSH_DELAY_MS));
  }

  function setLiveOcrFrozen(nextFrozen) {
    liveOcrFrozenRef.current = Boolean(nextFrozen);
    if (liveOcrFrozenRef.current && liveOcrTimerRef.current) {
      clearTimeout(liveOcrTimerRef.current);
      liveOcrTimerRef.current = null;
    }
  }

  function enqueueLiveLineUpdate(update) {
    const lineId = String(update?.lineId || "").trim();
    if (!lineId) return;

    const nextOp = {
      lineId,
      lineOrder: Number.isFinite(Number(update?.lineOrder)) ? Number(update.lineOrder) : 0,
      revision: Number.isFinite(Number(update?.revision)) ? Number(update.revision) : 0,
      clearLine: Boolean(update?.clearLine),
      dataUrl: update?.clearLine ? "" : String(update?.dataUrl || "")
    };

    const current = pendingLineUpdatesRef.current.get(lineId);
    if (current && Number(current.revision || 0) > nextOp.revision) return;

    pendingLineUpdatesRef.current.set(lineId, nextOp);
    if (!pendingLineOrderRef.current.includes(lineId)) {
      pendingLineOrderRef.current.push(lineId);
    }
    queueLiveLineOcr(update?.delay, { allowWhileFrozen: update?.allowWhileFrozen });
  }

  function getLaneBoundsFromRef() {
    return getLaneBounds(laneHeightsRef.current);
  }

  function getLaneStrokes(laneIndex, sourceLines = linesRef.current) {
    return (Array.isArray(sourceLines) ? sourceLines : []).filter(
      (stroke) => Number(stroke?.laneIndex) === Number(laneIndex)
    );
  }

  function getLaneInkExtents(laneIndex, sourceLines = linesRef.current) {
    const laneIdx = Number(laneIndex);
    if (!Number.isFinite(laneIdx)) return null;
    const bounds = getLaneBoundsFromRef();
    const lane = bounds.find((item) => item.index === laneIdx);
    if (!lane) return null;

    const strokes = getLaneStrokes(laneIdx, sourceLines);
    if (!strokes.length) return null;

    const laneHeight = Math.max(36, Math.ceil(lane.bottom - lane.top));
    const laneWidth = Math.max(40, Math.ceil(getEffectiveStageWidth()));

    if (!renderCanvasRef.current) {
      renderCanvasRef.current = document.createElement("canvas");
    }
    const laneCanvas = renderCanvasRef.current;
    const laneCtx = laneCanvas.getContext("2d");
    if (!laneCtx) return null;

    laneCanvas.width = laneWidth;
    laneCanvas.height = laneHeight;
    laneCtx.globalCompositeOperation = "source-over";
    laneCtx.fillStyle = "#ffffff";
    laneCtx.fillRect(0, 0, laneWidth, laneHeight);
    laneCtx.strokeStyle = "#000000";
    laneCtx.lineCap = "round";
    laneCtx.lineJoin = "round";

    for (const stroke of strokes) {
      const points = Array.isArray(stroke?.points) ? stroke.points : [];
      if (points.length < 4) continue;
      laneCtx.globalCompositeOperation = stroke?.tool === "eraser" ? "destination-out" : "source-over";
      laneCtx.lineWidth = Number(stroke?.width) || (stroke?.tool === "eraser" ? 28 : 2.5);
      laneCtx.beginPath();
      laneCtx.moveTo(points[0], points[1] - lane.top);
      for (let i = 2; i < points.length; i += 2) {
        laneCtx.lineTo(points[i], points[i + 1] - lane.top);
      }
      laneCtx.stroke();
    }

    const image = laneCtx.getImageData(0, 0, laneWidth, laneHeight);
    const px = image.data;
    let hasInk = false;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (let y = 0; y < laneHeight; y += 1) {
      for (let x = 0; x < laneWidth; x += 1) {
        const i = (y * laneWidth + x) * 4;
        const r = px[i];
        const g = px[i + 1];
        const b = px[i + 2];
        const a = px[i + 3];
        if (a > 0 && (r < 245 || g < 245 || b < 245)) {
          hasInk = true;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!hasInk) return null;
    return {
      laneTop: lane.top,
      laneBottom: lane.bottom,
      minY,
      maxY
    };
  }

  function laneHasPenInk(laneIndex, sourceLines = linesRef.current) {
    return !renderLaneImageData(laneIndex, sourceLines).clearLine;
  }

  function syncAttemptSlateContext(attempt, options = {}) {
    if (!attempt) return;
    setSlateDiagramSummary(getAttemptDiagramSummary(attempt));
    setOcrLaneRows(normalizeOcrLaneRowsFromAttempt(attempt));
    if (options.updateLaneMap) {
      setAnalysisLaneMap(buildMathLaneMapFromAttempt(attempt, linesRef.current, laneHasPenInk));
    }
  }
  function syncTutorLaneWithInk(sourceLines = linesRef.current) {
    if (stepClustersRef.current && stepClustersRef.current.length > 0) {
      setTutorLaneIndex(stepClustersRef.current.length);
      return;
    }
    const bounds = getLaneBoundsFromRef()
      .map((lane) => ({ ...lane, index: Number(lane.index) }))
      .filter((lane) => Number.isFinite(lane.index) && lane.index >= 0)
      .sort((a, b) => a.index - b.index);
    if (!bounds.length) {
      setTutorLaneIndex(0);
      return;
    }
    const firstEmptyLane = bounds.find((lane) => !laneHasPenInk(lane.index, sourceLines));
    setTutorLaneIndex(firstEmptyLane ? firstEmptyLane.index : bounds[bounds.length - 1].index);
  }

  function renderLaneImageData(laneIndex, sourceLines = linesRef.current) {
    const laneIdx = Number(laneIndex);
    if (!Number.isFinite(laneIdx)) return { clearLine: true, dataUrl: "" };
    const bounds = getLaneBoundsFromRef();
    const lane = bounds.find((item) => item.index === laneIdx);
    if (!lane) return { clearLine: true, dataUrl: "" };

    const strokes = getLaneStrokes(laneIdx, sourceLines);
    if (!strokes.length) return { clearLine: true, dataUrl: "" };

    const laneHeight = Math.max(36, Math.ceil(lane.bottom - lane.top));
    const laneWidth = Math.max(40, Math.ceil(getEffectiveStageWidth()));

    if (!renderCanvasRef.current) {
      renderCanvasRef.current = document.createElement("canvas");
    }
    const laneCanvas = renderCanvasRef.current;
    const laneCtx = laneCanvas.getContext("2d");
    if (!laneCtx) return { clearLine: true, dataUrl: "" };

    laneCanvas.width = laneWidth;
    laneCanvas.height = laneHeight;
    laneCtx.globalCompositeOperation = "source-over";
    laneCtx.fillStyle = "#ffffff";
    laneCtx.fillRect(0, 0, laneWidth, laneHeight);
    laneCtx.strokeStyle = "#000000";
    laneCtx.lineCap = "round";
    laneCtx.lineJoin = "round";

    for (const stroke of strokes) {
      const points = Array.isArray(stroke?.points) ? stroke.points : [];
      if (points.length < 4) continue;
      laneCtx.globalCompositeOperation = stroke?.tool === "eraser" ? "destination-out" : "source-over";
      laneCtx.lineWidth = Number(stroke?.width) || (stroke?.tool === "eraser" ? 28 : 2.5);
      laneCtx.beginPath();
      laneCtx.moveTo(points[0], points[1] - lane.top);
      for (let i = 2; i < points.length; i += 2) {
        laneCtx.lineTo(points[i], points[i + 1] - lane.top);
      }
      laneCtx.stroke();
    }

    const image = laneCtx.getImageData(0, 0, laneWidth, laneHeight);
    let hasInk = false;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    const px = image.data;
    for (let y = 0; y < laneHeight; y += 1) {
      for (let x = 0; x < laneWidth; x += 1) {
        const i = (y * laneWidth + x) * 4;
        const r = px[i];
        const g = px[i + 1];
        const b = px[i + 2];
        const a = px[i + 3];
        if (a > 0 && (r < 245 || g < 245 || b < 245)) {
          hasInk = true;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!hasInk) return { clearLine: true, dataUrl: "" };

    minX = Math.max(0, minX - LANE_OCR_PADDING);
    minY = Math.max(0, minY - LANE_OCR_PADDING);
    maxX = Math.min(laneWidth - 1, maxX + LANE_OCR_PADDING);
    maxY = Math.min(laneHeight - 1, maxY + LANE_OCR_PADDING);

    const width = Math.max(40, Math.ceil(maxX - minX + 1));
    const height = Math.max(36, Math.ceil(maxY - minY + 1));

    if (!renderCropCanvasRef.current) {
      renderCropCanvasRef.current = document.createElement("canvas");
    }
    const canvas = renderCropCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { clearLine: true, dataUrl: "" };

    canvas.width = width;
    canvas.height = height;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(laneCanvas, minX, minY, width, height, 0, 0, width, height);
    return { clearLine: false, dataUrl: canvas.toDataURL("image/png") };
  }

  function renderFullSlateImageData(sourceLines = linesRef.current) {
    const strokes = Array.isArray(sourceLines) ? sourceLines : [];
    if (!strokes.length) return "";

    const width = Math.max(40, Math.ceil(getEffectiveStageWidth()));
    const height = Math.max(36, Math.ceil(stageSize.height || 0));

    if (!renderFullCanvasRef.current) {
      renderFullCanvasRef.current = document.createElement("canvas");
    }
    const fullCanvas = renderFullCanvasRef.current;
    const fullCtx = fullCanvas.getContext("2d");
    if (!fullCtx) return "";

    fullCanvas.width = width;
    fullCanvas.height = height;
    fullCtx.globalCompositeOperation = "source-over";
    fullCtx.fillStyle = "#ffffff";
    fullCtx.fillRect(0, 0, width, height);
    fullCtx.strokeStyle = "#000000";
    fullCtx.lineCap = "round";
    fullCtx.lineJoin = "round";

    for (const stroke of strokes) {
      const points = Array.isArray(stroke?.points) ? stroke.points : [];
      if (points.length < 4) continue;
      fullCtx.globalCompositeOperation = stroke?.tool === "eraser" ? "destination-out" : "source-over";
      fullCtx.lineWidth = Number(stroke?.width) || (stroke?.tool === "eraser" ? 28 : 2.5);
      fullCtx.beginPath();
      fullCtx.moveTo(points[0], points[1]);
      for (let i = 2; i < points.length; i += 2) {
        fullCtx.lineTo(points[i], points[i + 1]);
      }
      fullCtx.stroke();
    }

    const image = fullCtx.getImageData(0, 0, width, height);
    const px = image.data;
    let hasInk = false;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const i = (y * width + x) * 4;
        const r = px[i];
        const g = px[i + 1];
        const b = px[i + 2];
        const a = px[i + 3];
        if (a > 0 && (r < 245 || g < 245 || b < 245)) {
          hasInk = true;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!hasInk) return "";

    minX = Math.max(0, minX - LANE_OCR_PADDING);
    minY = Math.max(0, minY - LANE_OCR_PADDING);
    maxX = Math.min(width - 1, maxX + LANE_OCR_PADDING);
    maxY = Math.min(height - 1, maxY + LANE_OCR_PADDING);

    const cropWidth = Math.max(40, Math.ceil(maxX - minX + 1));
    const cropHeight = Math.max(36, Math.ceil(maxY - minY + 1));

    if (!renderFullCropCanvasRef.current) {
      renderFullCropCanvasRef.current = document.createElement("canvas");
    }
    const cropCanvas = renderFullCropCanvasRef.current;
    const cropCtx = cropCanvas.getContext("2d");
    if (!cropCtx) return "";

    cropCanvas.width = cropWidth;
    cropCanvas.height = cropHeight;
    cropCtx.globalCompositeOperation = "source-over";
    cropCtx.fillStyle = "#ffffff";
    cropCtx.fillRect(0, 0, cropWidth, cropHeight);
    cropCtx.drawImage(fullCanvas, minX, minY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    return cropCanvas.toDataURL("image/png");
  }

  function enqueueLaneOcr(laneIndex, options = {}) {
    const idx = Number(laneIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    if (liveOcrFrozenRef.current && !options.allowWhileFrozen) return;
    const lineId = getLineIdForLane(idx);
    const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
    const nextRevision = options.bumpRevision ? currentRevision + 1 : currentRevision;
    laneRevisionRef.current[lineId] = nextRevision;

    const rendered = renderLaneImageData(idx);
    const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
    if (!options.bumpRevision && !options.force && nextRevision <= ackRevision) return;

    enqueueLiveLineUpdate({
      lineId,
      lineOrder: idx,
      revision: nextRevision,
      clearLine: rendered.clearLine,
      dataUrl: rendered.dataUrl,
      delay: options.delay
    });
  }

  function markLaneDirty(laneIndex) {
    const idx = Number(laneIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    const lineId = getLineIdForLane(idx);
    const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
    laneRevisionRef.current[lineId] = currentRevision + 1;
  }

  function enqueueDirtyLanes(maxLaneIndex, options = {}) {
    if (liveOcrFrozenRef.current && !options.allowWhileFrozen) return;
    const maxIdx = Number.isFinite(Number(maxLaneIndex)) ? Number(maxLaneIndex) : Number.MAX_SAFE_INTEGER;
    const bounds = getLaneBoundsFromRef();
    bounds.forEach((lane) => {
      const idx = Number(lane.index);
      if (!Number.isFinite(idx) || idx < 0 || idx > maxIdx) return;
      const lineId = getLineIdForLane(idx);
      const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
      const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
      if (currentRevision > ackRevision) {
        enqueueLaneOcr(idx, {
          force: true,
          bumpRevision: false,
          delay: options.delay ?? 100
        });
      }
    });
  }

  function appendBlankSlateLanes(count = SLATE_SCROLL_EXTEND_BATCH) {
    const laneCount = Math.max(1, Math.min(24, Math.floor(Number(count) || SLATE_SCROLL_EXTEND_BATCH)));
    setLaneHeights((prev) => [...prev, ...Array.from({ length: laneCount }, () => DEFAULT_LANE_HEIGHT)]);
  }

  function ensureTrailingLane(activeLaneIndex) {
    const laneIndex = Number(activeLaneIndex);
    if (!Number.isFinite(laneIndex) || laneIndex < 0) return;
    if (!laneHasPenInk(laneIndex)) return;
    setLaneHeights((prev) => {
      const trailingBlankCount = prev.length - laneIndex - 1;
      const missing = SLATE_TRAILING_BLANK_LANES - trailingBlankCount;
      if (missing <= 0) return prev;
      return [...prev, ...Array.from({ length: missing }, () => DEFAULT_LANE_HEIGHT)];
    });
  }

  function onLaneStrokeFinished(laneIndex) {
    const idx = Number(laneIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    const lineId = getLineIdForLane(idx);
    const nowHasInk = laneHasPenInk(idx);
    laneInkStateRef.current[lineId] = nowHasInk;

    // Plain canvas mode uses dynamic line-band step clustering.
    // Fixed row lane OCR MUST NOT run.
    const isPlainCanvasActive = true;
    if (isPlainCanvasActive) {
      return;
    }

    const hadInk = Boolean(laneInkStateRef.current[lineId]);
    const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
    const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
    const laneHasHistory = currentRevision > 0 || ackRevision > 0;
    const liveOcrFrozen = liveOcrFrozenRef.current;

    // Commit ONLY when this row transitions from empty -> ink.
    if (!liveOcrFrozen && !hadInk && nowHasInk && idx > 0 && !laneHasHistory) {
      enqueueLaneOcr(idx - 1, { force: true, bumpRevision: true, delay: 80 });
      enqueueDirtyLanes(idx - 1, { delay: 100 });
    }

    if (hadInk && nowHasInk) {
      markLaneDirty(idx);
    }
    if (hadInk && !nowHasInk) {
      markLaneDirty(idx);
    }

    ensureTrailingLane(idx);
    syncTutorLaneWithInk();
    onSlateStrokeFinished();
  }

  function syncInkClusters() {
    const canvasWidth = Math.max(MIN_STAGE_WIDTH, Number(stageCanvasWidth) || 800);
    const prevClusters = stepClustersRef.current || [];
    const clusters = updateStepClustersIncremental(prevClusters, linesRef.current, { canvasWidth });
    setStepClusters(clusters);
    stepClustersRef.current = clusters;
    return clusters;
  }

  function getClusterFingerprint(cluster) {
    if (!cluster || !Array.isArray(cluster.strokes)) return "";
    const strokeIds = cluster.strokes.map((s) => s.id || "").sort().join(",");
    const b = cluster.bounds || {};
    return `${strokeIds}:${Math.round(b.minX || 0)},${Math.round(b.minY || 0)},${Math.round(b.maxX || 0)},${Math.round(b.maxY || 0)}`;
  }

  function enqueueClusterOcr(cluster, options = {}) {
    if (!cluster || !cluster.id) return;
    if (liveOcrFrozenRef.current && !options.allowWhileFrozen) return;

    const fingerprint = getClusterFingerprint(cluster);
    const lastSent = clusterFingerprintsRef.current[cluster.id];

    // Avoid redundant OCR calls if ink in this step hasn't changed
    if (!options.force && lastSent === fingerprint) {
      return;
    }
    clusterFingerprintsRef.current[cluster.id] = fingerprint;

    const rendered = renderClusterImageData(cluster, {
      canvas: renderCropCanvasRef.current,
      padding: 28
    });

    const ackRev = Number(laneAckRevisionRef.current[cluster.id] || 0);
    const currentRev = Number(laneRevisionRef.current[cluster.id] || 0);
    const baseRev = Number(cluster.revision || 1);
    const nextRevision = Math.max(ackRev + 1, currentRev + 1, baseRev);
    laneRevisionRef.current[cluster.id] = nextRevision;

    enqueueLiveLineUpdate({
      lineId: cluster.id,
      lineOrder: Number.isFinite(Number(cluster.stepNo)) ? Math.max(0, Number(cluster.stepNo) - 1) : 0,
      revision: nextRevision,
      clearLine: rendered.clearLine,
      dataUrl: rendered.dataUrl,
      delay: options.delay ?? 50,
      allowWhileFrozen: options.allowWhileFrozen
    });
  }

  function onSlateStrokeFinished() {
    mathEditorUserEditedRef.current = false;
    setSelectedDiagStep(null);
    if (isCustom && activeCustomQuestionId) {
      saveSlateDraft(activeCustomQuestionId, false);
    }
    const clusters = syncInkClusters();
    setTutorLaneIndex(clusters.length);
    const liveOcrFrozen = liveOcrFrozenRef.current;
    if (liveOcrFrozen) return;

    // Detect and clear any erased / deleted clusters from Live OCR and Math Editor
    const activeClusterIdSet = new Set(clusters.map((c) => c.id));
    const previousClusterIds = Object.keys(clusterFingerprintsRef.current || {});
    for (const oldId of previousClusterIds) {
      if (!activeClusterIdSet.has(oldId)) {
        if (clusterDebounceTimersRef.current.has(oldId)) {
          clearTimeout(clusterDebounceTimersRef.current.get(oldId));
          clusterDebounceTimersRef.current.delete(oldId);
        }
        delete clusterFingerprintsRef.current[oldId];
        const nextRev = Math.max(
          Number(laneAckRevisionRef.current[oldId] || 0) + 1,
          Number(laneRevisionRef.current[oldId] || 0) + 1
        );
        laneRevisionRef.current[oldId] = nextRev;
        enqueueLiveLineUpdate({
          lineId: oldId,
          clearLine: true,
          revision: nextRev,
          delay: 20
        });
      }
    }

    if (!clusters.length) {
      previousMathClustersCountRef.current = 0;
      return;
    }

    const currentCount = clusters.length;
    const previousCount = previousMathClustersCountRef.current;
    previousMathClustersCountRef.current = currentCount;

    // Kinematic step break: new step line started!
    // Commit previous completed step line immediately to OCR.
    if (currentCount > 1 && currentCount > previousCount) {
      const finishedCluster = clusters[currentCount - 2];
      if (finishedCluster) {
        if (clusterDebounceTimersRef.current.has(finishedCluster.id)) {
          clearTimeout(clusterDebounceTimersRef.current.get(finishedCluster.id));
          clusterDebounceTimersRef.current.delete(finishedCluster.id);
        }
        enqueueClusterOcr(finishedCluster, { delay: 30 });
      }
    }

    // Debounce all clusters with changed ink (including edits/erasures in earlier steps)
    const changedClusters = clusters.filter(
      (c) => getClusterFingerprint(c) !== clusterFingerprintsRef.current[c.id]
    );

    for (const cluster of changedClusters) {
      if (clusterDebounceTimersRef.current.has(cluster.id)) {
        clearTimeout(clusterDebounceTimersRef.current.get(cluster.id));
      }
      const isTailCluster = cluster.id === clusters[clusters.length - 1]?.id;
      const debounceDelay = isTailCluster ? 750 : 1200;
      const timer = setTimeout(() => {
        clusterDebounceTimersRef.current.delete(cluster.id);
        enqueueClusterOcr(cluster, { delay: 40 });
      }, debounceDelay);
      clusterDebounceTimersRef.current.set(cluster.id, timer);
    }
  }


  async function ensureLiveAttemptId() {
    if (ocrAttemptIdRef.current) return ocrAttemptIdRef.current;
    if (!question) return "";

    const activeToken = tokenRef.current || token;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    let attemptRes;
    try {
      attemptRes = await fetch(`${apiBase}/api/attempts`, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${activeToken}`
        },
        body: JSON.stringify({
          frameId: question.frameId || null,
          questionId: question.id || activeCustomQuestionId,
          chapterId: question.chapterId || "custom",
          difficulty: question.difficulty || level || "HOT",
          assignmentSetId: assignmentMeta?.setId || customNav?.setId || null,
          isTestMode: isTestMode,
          questionDraft: (question.id || activeCustomQuestionId)
            ? undefined
            : {
              latex: question.latex,
              variables: question.variables || {},
              combinationId: question.combinationId || null,
              combinationLabel: question.combinationLabel || null,
              figureSpec: question.figureSpec || null,
              figureRenderSpec: question.figureRenderSpec || null,
              diagramSchema: question.diagramSchema || null,
              frameSchema: question.frameSchema || null
            },
          slateMeta: {
            live: true
          },
          onboardingDemo: !!isDemo
        })
      });
    } finally {
      clearTimeout(timer);
    }

    if (!attemptRes.ok) {
      const body = await attemptRes.json().catch(() => ({}));
      throw new Error(body.error || "Failed to create live attempt");
    }

    const attemptPayload = await attemptRes.json();
    const nextId = String(attemptPayload?.attempt?._id || "");
    if (!nextId) throw new Error("Live attempt ID missing");
    setOcrAttemptId(nextId);
    ocrAttemptIdRef.current = nextId;
    return nextId;
  }

  function openCollabInviteModal() {
    setCollabInviteOpen(true);
    setCollabInviteTarget("");
    setCollabInviteUrl("");
    setCollabInviteError("");
    setCollabInviteLookupNote("");
    setCollabInviteMatches([]);
    setCollabInviteSelectedUser(null);
    setCollabInviteResolvedUser(null);
  }

  function closeCollabInviteModal() {
    setCollabInviteOpen(false);
    setCollabInviteError("");
    setCollabInviteMatches([]);
    setCollabInviteLookupNote("");
  }

  function handleCollabInviteTargetChange(event) {
    const nextValue = String(event.target.value || "");
    const nextQuery = nextValue.trim().toLowerCase();
    setCollabInviteTarget(nextValue);
    setCollabInviteUrl("");
    setCollabInviteError("");
    setCollabInviteResolvedUser(null);

    if (
      collabInviteSelectedUser &&
      nextQuery !== String(collabInviteSelectedUser.username || "").trim().toLowerCase() &&
      nextQuery !== String(collabInviteSelectedUser.email || "").trim().toLowerCase()
    ) {
      setCollabInviteSelectedUser(null);
    }
  }

  function handleCollabInviteUserSelect(user) {
    if (!user) return;
    setCollabInviteSelectedUser(user);
    setCollabInviteResolvedUser(user);
    setCollabInviteTarget(user.username || user.email || "");
    setCollabInviteMatches([]);
    setCollabInviteLookupNote("Selected student will receive an in-app notification when you share the room.");
  }

  async function createCollaborationRoom() {
    if (collabInviteBusy || isDemo) return;
    setCollabInviteBusy(true);
    setCollabInviteError("");
    setCollabInviteUrl("");

    try {
      const attemptId = await ensureLiveAttemptId();
      if (!attemptId) throw new Error("Unable to initialize the shared attempt.");

      const response = await fetch(`${apiBase}/api/collaboration-sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attemptId,
          inviteTarget: collabInviteQuery || undefined,
          inviteeUserId: collabInviteSelectedUser?.uid || undefined
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Could not create the shared room.");
      }

      const payload = await response.json();
      const invitePath = payload?.invitePath || (payload?.session?.roomToken ? `/collaborate/${payload.session.roomToken}` : "");
      if (!invitePath) throw new Error("Invite link missing from server response.");
      const nextUrl = typeof window !== "undefined" ? `${window.location.origin}${invitePath}` : invitePath;
      setCollabInviteUrl(nextUrl);
      setCollabInviteResolvedUser(payload?.inviteeMatched ? payload?.invitee || collabInviteSelectedUser : null);
      setCollabInviteLookupNote(
        payload?.inviteeMatched
          ? "Notification sent to the selected student. You can also share the room link below."
          : "Room created. You can now share the room link below."
      );
      if (typeof window !== "undefined") {
        try {
          window.open(nextUrl, "_blank", "noopener,noreferrer");
        } catch (err) {
          // If the new tab is blocked, we still show the copied link below.
        }
      }
      try {
        await navigator.clipboard?.writeText(nextUrl);
      } catch (err) {
        // Clipboard can fail on older tablet browsers; showing the link is enough.
      }
    } catch (err) {
      setCollabInviteError(err.message || "Could not create the shared room.");
    } finally {
      setCollabInviteBusy(false);
    }
  }

  async function handleCollabNativeShare() {
    if (!collabInviteUrl) return;
    setCollabInviteError("");
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: "Ink Together room",
          text: "Join me on Ink Together for this question.",
          url: collabInviteUrl
        });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }

    try {
      await navigator.clipboard?.writeText(collabInviteUrl);
      setCollabInviteLookupNote("Invite link copied. Share it anywhere.");
    } catch (err) {
      setCollabInviteLookupNote("Open Gmail or WhatsApp below to share this room.");
    }
  }

  function handleCollabMailShare() {
    if (!collabInviteMailToHref || typeof window === "undefined") return;
    window.location.href = collabInviteMailToHref;
  }

  function handleCollabWhatsAppShare() {
    if (!collabInviteWhatsAppHref) return;
    if (typeof window === "undefined" || typeof navigator === "undefined") return;
    const userAgent = String(navigator.userAgent || "").toLowerCase();
    const isMobileAppCapable = /android|iphone|ipad|ipod/i.test(userAgent);
    if (isMobileAppCapable) {
      window.location.href = collabInviteWhatsAppHref;
      return;
    }
    window.open(collabInviteWhatsAppWebHref, "_blank", "noopener,noreferrer");
  }

  function hasRenderableFigureAssist(result) {
    const preview = result?.preview && typeof result.preview === "object" ? result.preview : {};
    const previewSegments = Array.isArray(preview.segments) ? preview.segments : [];
    const previewCircles = Array.isArray(preview.circles) ? preview.circles : [];
    return previewSegments.length > 0 || previewCircles.length > 0;
  }

  async function uploadSlateSnapshot(attemptId, dataUrl) {
    if (!attemptId || !dataUrl) return null;

    const uniqueSlateName = `slate_${question?.id || question?.frameId || "draft"}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}.png`;

    const uploadRes = await fetch(`${apiBase}/api/uploads/slate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        dataUrl,
        fileName: uniqueSlateName,
        contentType: "image/png"
      })
    });

    if (!uploadRes.ok) {
      const body = await uploadRes.json().catch(() => ({}));
      throw new Error(body.error || "Slate upload failed");
    }

    const uploadPayload = await uploadRes.json();
    const slatePatchRes = await fetch(`${apiBase}/api/attempts/${attemptId}/slate`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        slateUrl: uploadPayload.gsUrl,
        slateMeta: {
          path: uploadPayload.path,
          lines: linesRef.current,
          stageWidth: Math.max(40, Math.ceil(getEffectiveStageWidth())),
          stageHeight: Math.max(36, Math.ceil(stageSize.height || 0)),
          laneHeights: Array.isArray(laneHeightsRef.current) ? laneHeightsRef.current : []
        }
      })
    });
    if (!slatePatchRes.ok) {
      const body = await slatePatchRes.json().catch(() => ({}));
      throw new Error(body.error || "Failed to update attempt slate");
    }

    return uploadPayload;
  }

  async function acceptStudentFigureAssist(result) {
    const attemptId = await ensureLiveAttemptId();
    if (!attemptId) throw new Error("Unable to initialize attempt");

    const response = await fetch(`${apiBase}/api/attempts/${attemptId}/figure`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        action: "save",
        dataUrl: result?.dataUrl || null,
        sourceLines: result?.sourceLines || [],
        sourceMeta: result?.sourceMeta || {},
        primitives: result?.primitives || null,
        figureSpec: result?.figureSpec || null,
        figureRenderSpec: result?.figureRenderSpec || result?.figureSpec || null,
        diagramSchema: result?.diagramSchema || null,
        summary: result?.summary || null,
        warnings: result?.warnings || [],
        confidence: result?.confidence || null
      })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || "Failed to save figure capture");
    }

    const nextFigureAssist = payload.figureAssist || {
      ...result,
      status: "accepted"
    };
    studentFigureAssistRef.current = nextFigureAssist;
    draftFigureAssistRef.current = nextFigureAssist;
    lastAcceptedFigureDraftRef.current = nextFigureAssist;
    setStudentFigureAssist(nextFigureAssist);
    setDraftFigureAssist(nextFigureAssist);
    return nextFigureAssist;
  }

  async function rejectStudentFigureAssist() {
    if (!ocrAttemptIdRef.current && !studentFigureAssist?.sampleId) {
      setStudentFigureAssist(null);
      setDraftFigureAssist(null);
      return null;
    }
    const attemptId = ocrAttemptIdRef.current || (await ensureLiveAttemptId());
    if (!attemptId) return null;

    const response = await fetch(`${apiBase}/api/attempts/${attemptId}/figure`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        action: "clear"
      })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || "Failed to clear figure capture");
    }

    studentFigureAssistRef.current = null;
    draftFigureAssistRef.current = null;
    lastAcceptedFigureDraftRef.current = null;
    figureAcceptInFlightRef.current = null;
    setStudentFigureAssist(null);
    setDraftFigureAssist(null);
    return null;
  }

  const handlePreviewFigureAssist = useCallback((result) => {
    clearTutorHint();
    if (hasRenderableFigureAssist(result)) {
      setDraftFigureAssist(result);
    } else {
      setDraftFigureAssist(null);
    }
  }, [clearTutorHint]);

  async function ensureDraftFigureAssistAccepted(options = {}) {
    const awaitCompletion = Boolean(options?.awaitCompletion);
    const draft = draftFigureAssistRef.current;
    if (!showFigureAssistPanel || !hasRenderableFigureAssist(draft)) return null;
    if (
      draft === lastAcceptedFigureDraftRef.current &&
      String(studentFigureAssistRef.current?.status || "").toLowerCase() === "accepted"
    ) {
      return studentFigureAssistRef.current;
    }

    const inFlight = figureAcceptInFlightRef.current;
    if (inFlight?.draft === draft && inFlight?.promise) {
      return awaitCompletion ? inFlight.promise : inFlight.promise.catch(() => null);
    }

    const promise = Promise.resolve(acceptStudentFigureAssist(draft)).finally(() => {
      if (figureAcceptInFlightRef.current?.promise === promise) {
        figureAcceptInFlightRef.current = null;
      }
    });
    figureAcceptInFlightRef.current = { draft, promise };
    if (awaitCompletion) return promise;
    promise.catch(() => { });
    return promise;
  }

  async function flushLiveLineOcr(options = {}) {
    const allowWhileFrozen = Boolean(options?.allowWhileFrozen);
    if (liveOcrFrozenRef.current && !allowWhileFrozen) return;
    if (liveOcrInFlightRef.current) {
      if (pendingLineOrderRef.current.length && (!liveOcrFrozenRef.current || allowWhileFrozen)) {
        queueLiveLineOcr(LIVE_OCR_MIN_DELAY_MS, { allowWhileFrozen });
      }
      return;
    }
    if (!pendingLineOrderRef.current.length) return;
    if (!question) return;

    liveOcrInFlightRef.current = true;
    const sessionId = liveSessionRef.current;
    updateLiveOcrStatus("Reading what you wrote...");

    let selectedLineId = null;
    let pendingOp = null;

    try {
      const attemptId = await ensureLiveAttemptId();
      if (sessionId !== liveSessionRef.current) {
        updateLiveOcrStatus("", { force: true });
        return;
      }
      selectedLineId = pendingLineOrderRef.current[0];
      pendingOp = pendingLineUpdatesRef.current.get(selectedLineId);
      if (!pendingOp) {
        pendingLineOrderRef.current.shift();
        if (pendingLineOrderRef.current.length && (!liveOcrFrozenRef.current || allowWhileFrozen)) {
          queueLiveLineOcr(LIVE_OCR_MIN_DELAY_MS, { allowWhileFrozen });
        } else {
          updateLiveOcrStatus("", { force: true });
        }
        return;
      }

      // Pop the currently processed line from the front of the queue immediately
      pendingLineOrderRef.current.shift();

      const activeLineIds = (stepClustersRef.current || []).map((c) => c.id);
      const requestBody = {
        lineId: selectedLineId,
        lineOrder: Number.isFinite(Number(pendingOp?.lineOrder)) ? Number(pendingOp.lineOrder) : 0,
        revision: Number.isFinite(Number(pendingOp?.revision)) ? Number(pendingOp.revision) : 0,
        ...(activeLineIds.length ? { activeLineIds } : {})
      };
      if (pendingOp.clearLine) requestBody.clearLine = true;
      else requestBody.dataUrl = pendingOp.dataUrl;

      const controller = new AbortController();
      const fetchTimer = setTimeout(() => controller.abort(), 28000);
      let response;
      try {
        response = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr-line`, {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenRef.current || token}`
          },
          body: JSON.stringify(requestBody)
        });
      } finally {
        clearTimeout(fetchTimer);
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to read line");
      }

      const payload = await response.json();
      if (sessionId !== liveSessionRef.current) {
        updateLiveOcrStatus("", { force: true });
        return;
      }

      const stale = Boolean(payload?.ocr?.line?.stale);
      const confidence = String(payload?.ocr?.line?.confidence || "").toLowerCase();
      const contentKind = String(payload?.ocr?.line?.contentKind || "").toLowerCase();
      const lineLatex = String(payload?.ocr?.line?.latex || "");
      const lowQualityLine = confidence === "low" || /\[unclear\]/i.test(lineLatex);
      if (!stale && payload?.attempt) {
        syncAttemptSlateContext(payload.attempt);
      }
      if (!stale && !lowQualityLine) {
        const latestEdited = payload?.ocr?.editedLatex || "";
        const latestSteps = payload?.ocr?.editedSteps || [];
        const normalizedLatex = normalizeOcrLatexForEditor(latestEdited);
        applyLiveOcrEditorUpdate(
          normalizedLatex || latestEdited || "",
          Boolean((latestSteps || []).length || (latestEdited || "").trim())
        );
      }

      lineConfidenceRef.current[selectedLineId] = confidence || null;
      updateLiveOcrStatus(
        stale
          ? ""
          : confidence === "low"
            ? "Writing looks a bit unclear; final Analyze will review carefully."
            : contentKind === "diagram"
              ? "Understood your diagram"
              : contentKind === "mixed"
                ? "Read your math and diagram"
                : "Step saved"
      );

      const acknowledgedRevision = Number(payload?.ocr?.line?.revision ?? pendingOp.revision ?? 0);
      laneAckRevisionRef.current[selectedLineId] = Math.max(
        Number(laneAckRevisionRef.current[selectedLineId] || 0),
        acknowledgedRevision
      );

      const currentPending = pendingLineUpdatesRef.current.get(selectedLineId);
      if (currentPending === pendingOp) {
        pendingLineUpdatesRef.current.delete(selectedLineId);
      } else if (currentPending) {
        pendingLineOrderRef.current.push(selectedLineId);
      }
    } catch (err) {
      updateLiveOcrStatus("Couldn't read that line clearly. Try rewriting.", { force: true });
      if (selectedLineId) {
        delete clusterFingerprintsRef.current[selectedLineId];
        const currentPending = pendingLineUpdatesRef.current.get(selectedLineId);
        if (currentPending === pendingOp) {
          pendingLineUpdatesRef.current.delete(selectedLineId);
        }
        pendingLineOrderRef.current = pendingLineOrderRef.current.filter((id) => id !== selectedLineId);
      }
    } finally {
      liveOcrInFlightRef.current = false;
      if (pendingLineOrderRef.current.length && (!liveOcrFrozenRef.current || allowWhileFrozen)) {
        queueLiveLineOcr(LIVE_OCR_MIN_DELAY_MS, { allowWhileFrozen });
      }
    }
  }

  async function flushAllPendingLiveOcr(options = {}) {
    const start = Date.now();
    const maxWaitMs = Number(options?.maxWaitMs) || 45000;
    while (Date.now() - start < maxWaitMs) {
      if (!pendingLineOrderRef.current.length && !liveOcrInFlightRef.current) break;
      if (!liveOcrInFlightRef.current && pendingLineOrderRef.current.length) {
        // eslint-disable-next-line no-await-in-loop
        await flushLiveLineOcr({ allowWhileFrozen: true });
      } else {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }
  }

  function handleSetWorkTab(nextTab) {
    const target = typeof nextTab === "function" ? nextTab(workTab) : nextTab;
    if (target === "editor") {
      if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
        clusterDebounceTimersRef.current.forEach((timer, clusterId) => {
          clearTimeout(timer);
          const cluster = (stepClustersRef.current || []).find((c) => c.id === clusterId);
          if (cluster) {
            enqueueClusterOcr(cluster, { delay: 0, force: true, allowWhileFrozen: true });
          }
        });
        clusterDebounceTimersRef.current.clear();
      }
      flushAllPendingLiveOcr({ allowWhileFrozen: true }).catch(() => {});
    }
    setWorkTab(target);
  }

  async function finalizeLiveOcrBeforeAnalyze() {
    if (activeStrokeMetaRef.current?.tool === "pen") {
      if (drawRafRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(drawRafRef.current);
        drawRafRef.current = null;
      }
      flushBufferedDrawPoints();
      const laneIndex = Number(activeLaneRef.current);
      activeLaneRef.current = null;
      activePointerIdRef.current = null;
      activePointerTypeRef.current = "";
      pointerOwnershipRef.current.reset();
      setIsDrawing(false);
      setSlatePointerLock(false);
      const stroke = commitActivePenStroke();
      if (stroke && Number.isFinite(laneIndex)) {
        onLaneStrokeFinished(laneIndex);
      }
    }

    const isPlainCanvasActive = true;
    if (!isPlainCanvasActive) {
      const bounds = getLaneBoundsFromRef();
      bounds.forEach((lane) => {
        const lineId = getLineIdForLane(lane.index);
        const hasInk = laneHasPenInk(lane.index);
        const hadInk = Boolean(laneInkStateRef.current[lineId]);
        const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
        const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
        laneInkStateRef.current[lineId] = hasInk;

        if (currentRevision > ackRevision) {
          enqueueLaneOcr(lane.index, {
            force: true,
            bumpRevision: false,
            delay: 40,
            allowWhileFrozen: true
          });
          return;
        }

        if (hasInk && currentRevision === 0) {
          enqueueLaneOcr(lane.index, {
            force: true,
            bumpRevision: true,
            delay: 40,
            allowWhileFrozen: true
          });
          return;
        }
        if (!hasInk && hadInk) {
          enqueueLaneOcr(lane.index, {
            force: true,
            bumpRevision: true,
            delay: 40,
            allowWhileFrozen: true
          });
        }
      });
    }

    // Flush any pending cluster debounce timers immediately
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((t) => clearTimeout(t));
      clusterDebounceTimersRef.current.clear();
    }

    const clusters = syncInkClusters();

    // Detect and clear any erased / deleted clusters
    const activeClusterIdSet = new Set(clusters.map((c) => c.id));
    const previousClusterIds = Object.keys(clusterFingerprintsRef.current || {});
    for (const oldId of previousClusterIds) {
      if (!activeClusterIdSet.has(oldId)) {
        delete clusterFingerprintsRef.current[oldId];
        const nextRev = Math.max(
          Number(laneAckRevisionRef.current[oldId] || 0) + 1,
          Number(laneRevisionRef.current[oldId] || 0) + 1
        );
        laneRevisionRef.current[oldId] = nextRev;
        enqueueLiveLineUpdate({
          lineId: oldId,
          clearLine: true,
          revision: nextRev,
          delay: 10,
          allowWhileFrozen: true
        });
      }
    }

    clusters.forEach((cluster) => {
      const fingerprint = getClusterFingerprint(cluster);
      const ackRevision = Number(laneAckRevisionRef.current[cluster.id] || 0);
      const clusterRevision = Number(cluster.revision || 1);
      const isAcknowledged = ackRevision >= clusterRevision;
      const isUnchanged = clusterFingerprintsRef.current[cluster.id] === fingerprint;
      const isPending = pendingLineUpdatesRef.current.has(cluster.id);

      // Only enqueue if the cluster has unsent ink changes, is unacknowledged, or is currently pending
      if (!isAcknowledged || !isUnchanged || isPending) {
        enqueueClusterOcr(cluster, { delay: 10, allowWhileFrozen: true, force: true });
      }
    });

    await flushAllPendingLiveOcr({ allowWhileFrozen: true });
  }

  const getSlateInteractionRect = useCallback(() => {
    const overlayRect = activeStrokeCanvasRef.current?.getBoundingClientRect?.();
    if (overlayRect?.width && overlayRect?.height) return overlayRect;
    const stageRect = stageRef.current?.container?.()?.getBoundingClientRect?.();
    if (stageRect?.width && stageRect?.height) return stageRect;
    const paneRect = canvasPaneRef.current?.getBoundingClientRect?.();
    return paneRect?.width && paneRect?.height ? paneRect : null;
  }, []);

  const getSlatePointFromClient = useCallback((clientX, clientY) => {
    const rect = getSlateInteractionRect();
    if (!rect || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    const width = Math.max(MIN_STAGE_WIDTH, Math.floor(getEffectiveStageWidth()));
    const height = Math.max(1, Math.ceil(stageSize.height || 0));
    const scaleX = rect.width > 0 ? width / rect.width : 1;
    const scaleY = rect.height > 0 ? height / rect.height : 1;
    const rawX = (clientX - rect.left + Number(slateProfileConfig.nibOffsetX || 0)) * scaleX;
    const rawY = (clientY - rect.top + Number(slateProfileConfig.nibOffsetY || 0)) * scaleY;
    const x = Math.max(0, Math.min(width, rawX));
    const y = Math.max(0, Math.min(height, rawY));
    return { x, y };
  }, [
    getEffectiveStageWidth,
    getSlateInteractionRect,
    slateProfileConfig.nibOffsetX,
    slateProfileConfig.nibOffsetY,
    stageSize.height
  ]);

  const resetActivePointerState = useCallback(() => {
    activeLaneRef.current = null;
    activePointerIdRef.current = null;
    activePointerTypeRef.current = "";
  }, []);

  const shouldRejectResizeTouch = useCallback((event) => {
    const pointerType = String(event?.pointerType || "").toLowerCase();
    if (pointerType !== "touch") return false;
    const width = Number(event?.width || 0);
    const height = Number(event?.height || 0);
    const contact = Math.max(width, height, 0);
    const threshold = Number(slateProfileConfig.palmTouchContactPx || 0);
    const hardThreshold = Number(slateProfileConfig.palmHardRejectContactPx || threshold * 3.8);
    if (pointerOwnershipRef.current.getOwnerId() !== null) return true;
    if (event?.isPrimary === false) return true;
    if (slateProfileConfig.rejectWidePrimaryTouch && threshold > 0 && contact >= threshold * 1.15) return true;
    if (hardThreshold > 0 && contact >= hardThreshold) return true;
    return false;
  }, [slateProfileConfig]);

  const getLatestBufferedPoint = useCallback(() => {
    const pending = drawPendingPointsRef.current;
    if (pending.length >= 2) {
      return {
        x: Number(pending[pending.length - 2]),
        y: Number(pending[pending.length - 1])
      };
    }
    if (activeStrokeMetaRef.current?.tool === "pen") {
      const activePoints = activeStrokePointsRef.current;
      if (activePoints.length >= 2) {
        return {
          x: Number(activePoints[activePoints.length - 2]),
          y: Number(activePoints[activePoints.length - 1])
        };
      }
    }
    const lastStroke = linesRef.current[linesRef.current.length - 1];
    const lastPoints = Array.isArray(lastStroke?.points) ? lastStroke.points : [];
    if (lastPoints.length >= 2) {
      return {
        x: Number(lastPoints[lastPoints.length - 2]),
        y: Number(lastPoints[lastPoints.length - 1])
      };
    }
    return null;
  }, []);

  function getLaneFromPointerEvent(pointerEvent) {
    const pos = getSlatePointFromClient(pointerEvent?.clientX, pointerEvent?.clientY);
    if (!pos) return null;
    const bounds = getLaneBoundsFromRef();
    const laneIndex = getLaneIndexByY(pos.y, bounds);
    const lane = bounds.find((item) => item.index === laneIndex) || null;
    if (!lane) return null;
    return { pos, laneIndex, lane };
  }

  const syncActiveStrokeCanvas = useCallback(() => {
    const canvas = activeStrokeCanvasRef.current;
    if (!canvas) return null;
    const width = Math.max(MIN_STAGE_WIDTH, Math.floor(getEffectiveStageWidth()));
    const height = Math.max(1, Math.ceil(stageSize.height || 0));
    const dpr = typeof window !== "undefined" ? Math.max(1, Number(window.devicePixelRatio) || 1) : 1;

    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.imageSmoothingEnabled = true;
    return ctx;
  }, [getEffectiveStageWidth, stageSize.height]);

  const clearActiveStrokePreview = useCallback(() => {
    const ctx = syncActiveStrokeCanvas();
    if (!ctx) return;
    ctx.clearRect(0, 0, Math.max(MIN_STAGE_WIDTH, Math.floor(getEffectiveStageWidth())), Math.ceil(stageSize.height || 0));
  }, [getEffectiveStageWidth, stageSize.height, syncActiveStrokeCanvas]);

  const drawActiveStrokePreview = useCallback(() => {
    const meta = activeStrokeMetaRef.current;
    const points = activeStrokePointsRef.current;
    const ctx = syncActiveStrokeCanvas();
    if (!ctx) return;

    const width = Math.max(MIN_STAGE_WIDTH, Math.floor(getEffectiveStageWidth()));
    const height = Math.ceil(stageSize.height || 0);
    ctx.clearRect(0, 0, width, height);
    if (!meta || meta.tool !== "pen" || points.length < 2) return;

    ctx.save();
    ctx.strokeStyle = meta.color || strokeColor;
    ctx.fillStyle = meta.color || strokeColor;
    ctx.lineWidth = meta.width || 2.5;

    if (points.length === 2) {
      ctx.beginPath();
      ctx.arc(points[0], points[1], Math.max(1, (meta.width || 2.5) / 2), 0, Math.PI * 2);
      ctx.fill();
    } else {
      const pointPairs = [];
      for (let i = 0; i < points.length; i += 2) {
        pointPairs.push({ x: points[i], y: points[i + 1] });
      }
      ctx.beginPath();
      ctx.moveTo(pointPairs[0].x, pointPairs[0].y);
      if (pointPairs.length >= 2) {
        for (let i = 1; i < pointPairs.length; i += 1) {
          ctx.lineTo(pointPairs[i].x, pointPairs[i].y);
        }
      }
      ctx.stroke();
    }
    ctx.restore();
  }, [
    getEffectiveStageWidth,
    stageSize.height,
    strokeColor,
    syncActiveStrokeCanvas
  ]);

  const commitActivePenStroke = useCallback(() => {
    const meta = activeStrokeMetaRef.current;
    const points = activeStrokePointsRef.current.slice();
    activeStrokeMetaRef.current = null;
    activeStrokePointsRef.current = [];
    activeStrokePointProcessorRef.current = null;
    clearActiveStrokePreview();

    if (!meta || meta.tool !== "pen" || points.length < 2) return null;
    const committedPoints =
      points.length >= 4 ? points : [points[0], points[1], points[0] + 0.01, points[1] + 0.01];
    const stroke = {
      ...meta,
      points: committedPoints
    };
    const next = [...linesRef.current, stroke];
    linesRef.current = next;
    setLines(next);
    return stroke;
  }, [clearActiveStrokePreview]);

  const appendDrawPoint = useCallback((x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
    const pending = drawPendingPointsRef.current;

    const lastPoint = getLatestBufferedPoint();
    const lastX = Number(lastPoint?.x);
    const lastY = Number(lastPoint?.y);

    if (Number.isFinite(lastX) && Number.isFinite(lastY)) {
      const dx = x - lastX;
      const dy = y - lastY;
      const minDistance = Number(slateProfileConfig.minPointDistance || 0.75);
      if (dx * dx + dy * dy < minDistance * minDistance) {
        return false;
      }
    }

    pending.push(x, y);
    return true;
  }, [getLatestBufferedPoint, slateProfileConfig.minPointDistance]);

  const flushBufferedDrawPoints = useCallback(() => {
    if (!drawPendingPointsRef.current.length) return;
    const pointsToAppend = drawPendingPointsRef.current.slice();
    drawPendingPointsRef.current = [];

    if (activeStrokeMetaRef.current?.tool === "pen") {
      activeStrokePointsRef.current = activeStrokePointsRef.current.concat(pointsToAppend);
      drawActiveStrokePreview();
      return;
    }

    setLines((prevLines) => {
      const lastStroke = prevLines[prevLines.length - 1];
      if (!lastStroke) return prevLines;
      const updatedStroke = {
        ...lastStroke,
        points: lastStroke.points.concat(pointsToAppend)
      };
      const next = [...prevLines.slice(0, -1), updatedStroke];
      linesRef.current = next;
      return next;
    });
  }, [drawActiveStrokePreview]);

  const scheduleBufferedDrawFlush = useCallback(() => {
    if (drawRafRef.current) return;
    if (typeof window === "undefined") {
      flushBufferedDrawPoints();
      return;
    }
    drawRafRef.current = window.requestAnimationFrame(() => {
      drawRafRef.current = null;
      flushBufferedDrawPoints();
    });
  }, [flushBufferedDrawPoints]);

  const appendPointerSamples = useCallback((nativeEvent, lane, options = {}) => {
    if (!nativeEvent) return false;
    const limit = Number(slateProfileConfig.coalescedLimit || 64);
    const pointEvents = options.final
      ? [nativeEvent]
      : options.raw
        ? [nativeEvent]
        : getCoalescedPointerSamples(nativeEvent, limit);

    let appended = false;
    pointEvents.forEach((pointEvent) => {
      if (!pointEvent) return;
      const pos = getSlatePointFromClient(pointEvent.clientX, pointEvent.clientY);
      if (!pos) return;
      const rawY = pos.y;
      const processed = activeStrokePointProcessorRef.current?.process(
        pos.x,
        rawY,
        pointEvent.timeStamp,
        { final: Boolean(options.final) }
      );
      const nextX = Number(processed?.x ?? pos.x);
      const nextY = Number(processed?.y ?? rawY);
      const previousPoint = getLatestBufferedPoint();
      const interpolationSegmentLength = Number(
        slateProfileConfig.interpolationSegmentLength || 0
      );
      if (
        previousPoint &&
        interpolationSegmentLength > 0 &&
        Number.isFinite(previousPoint.x) &&
        Number.isFinite(previousPoint.y)
      ) {
        const dx = nextX - previousPoint.x;
        const dy = nextY - previousPoint.y;
        const distance = Math.hypot(dx, dy);
        const segmentCount = Math.min(
          12,
          Math.max(0, Math.floor(distance / interpolationSegmentLength) - 1)
        );
        for (let segmentIndex = 1; segmentIndex <= segmentCount; segmentIndex += 1) {
          const ratio = segmentIndex / (segmentCount + 1);
          const intermediateX = previousPoint.x + dx * ratio;
          const intermediateY = previousPoint.y + dy * ratio;
          if (appendDrawPoint(intermediateX, intermediateY)) appended = true;
        }
      }
      if (appendDrawPoint(nextX, nextY)) appended = true;
    });

    if (appended) {
      scheduleBufferedDrawFlush();
    }
    return appended;
  }, [
    appendDrawPoint,
    getLatestBufferedPoint,
    getSlatePointFromClient,
    scheduleBufferedDrawFlush,
    slateProfileConfig.coalescedLimit,
    slateProfileConfig.interpolationSegmentLength
  ]);

  function beginOwnedSlateStroke(action, captureTarget = null) {
    const samples = Array.isArray(action?.samples) ? action.samples : [];
    const firstSample = samples[0];
    const laneInfo = getLaneFromPointerEvent(firstSample);
    if (!firstSample || !laneInfo || laneDragRef.current) return false;

    if (action.replacedPointerId !== null && action.replacedPointerId !== undefined) {
      if (drawRafRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(drawRafRef.current);
        drawRafRef.current = null;
      }
      drawPendingPointsRef.current = [];
      const replacedStroke = activeStrokeMetaRef.current;
      if (replacedStroke?.tool === "eraser") {
        const next = linesRef.current.filter((stroke) => stroke.id !== replacedStroke.id);
        linesRef.current = next;
        setLines(next);
      }
      activeStrokeMetaRef.current = null;
      activeStrokePointsRef.current = [];
      activeStrokePointProcessorRef.current = null;
      resetActivePointerState();
      clearActiveStrokePreview();
      try {
        captureTarget?.releasePointerCapture?.(action.replacedPointerId);
      } catch (err) {
        // The browser can release the provisional palm capture itself.
      }
    }

    clearTutorHint();
    if (!liveOcrFrozenRef.current && Number.isFinite(Number(laneInfo.laneIndex)) && laneInfo.laneIndex > 0) {
      enqueueDirtyLanes(laneInfo.laneIndex - 1, { delay: 90 });
    }
    void ensureDraftFigureAssistAccepted();
    activePointerIdRef.current = action.pointerId;
    activePointerTypeRef.current = action.pointerType;
    if (captureTarget?.setPointerCapture && action.pointerId !== null) {
      try {
        captureTarget.setPointerCapture(action.pointerId);
      } catch (err) {
        // Ignore capture failures; drawing still works without it.
      }
    }
    setSlatePointerLock(true);

    const snapY = laneInfo.pos.y;
    activeLaneRef.current = laneInfo.laneIndex;
    drawPendingPointsRef.current = [];
    if (drawRafRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = null;
    }
    setIsDrawing(true);
    setRedoStack([]);
    const nextStroke = {
      id: `stroke_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      laneIndex: laneInfo.laneIndex,
      tool: strokeTool,
      color: strokeColor,
      width: strokeTool === "eraser" ? 28 : slateProfileConfig.lineWidth
    };
    activeStrokeMetaRef.current = nextStroke;
    activeStrokePointProcessorRef.current = strokeTool === "pen"
      ? createInkPointProcessor(slateProfileConfig)
      : null;
    const initialPoint = activeStrokePointProcessorRef.current?.process(
      laneInfo.pos.x,
      snapY,
      firstSample.timeStamp
    );
    activeStrokePointsRef.current = [
      Number(initialPoint?.x ?? laneInfo.pos.x),
      Number(initialPoint?.y ?? snapY)
    ];
    if (strokeTool === "pen") {
      clearActiveStrokePreview();
      drawActiveStrokePreview();
      samples.slice(1).forEach((sample) => {
        appendPointerSamples(sample, laneInfo.lane, { raw: true });
      });
      return true;
    }
    setLines((prev) => {
      const next = [
        ...prev,
        {
          ...nextStroke,
          points: [laneInfo.pos.x, snapY]
        }
      ];
      linesRef.current = next;
      return next;
    });
    samples.slice(1).forEach((sample) => {
      appendPointerSamples(sample, laneInfo.lane, { raw: true });
    });
    return true;
  }

  function handlePointerDown(event) {
    if (isExamLocked || isExamNotStarted) return;
    if (strokeTool === "scroll" || laneDragRef.current) return;
    const nativeEvent = event?.evt;
    nativeEvent?.preventDefault?.();
    const action = pointerOwnershipRef.current.pointerDown(nativeEvent);
    if (action.type === "start") {
      beginOwnedSlateStroke(action, nativeEvent?.target);
    }
  }

  function handlePointerMove(event) {
    if (isExamLocked || isExamNotStarted) return;
    const nativeEvent = event?.evt;
    const action = pointerOwnershipRef.current.pointerMove(nativeEvent);
    if (action.type === "start") {
      nativeEvent?.preventDefault?.();
      beginOwnedSlateStroke(action, nativeEvent?.target);
      return;
    }
    if (action.type !== "move" || action.pointerId !== activePointerIdRef.current) return;
    nativeEvent?.preventDefault?.();
    const laneIndex = Number(activeLaneRef.current);
    const bounds = getLaneBoundsFromRef();
    const lane = bounds.find((item) => item.index === laneIndex);
    appendPointerSamples(nativeEvent, lane);
  }

  function handlePointerUp(event, options = {}) {
    const nativeEvent = event?.evt;
    const pointerId = getNativePointerId(nativeEvent);
    const action = pointerOwnershipRef.current.pointerUp(nativeEvent, options);
    if (
      nativeEvent?.target?.releasePointerCapture &&
      Number.isFinite(Number(pointerId))
    ) {
      try {
        nativeEvent.target.releasePointerCapture(pointerId);
      } catch (err) {
        // no-op
      }
    }
    if (action.type === "tap") {
      if (!beginOwnedSlateStroke(action, nativeEvent?.target)) return;
    } else if (
      !["end", "cancel"].includes(action.type) ||
      action.pointerId !== activePointerIdRef.current
    ) {
      return;
    }
    const laneIndex = Number(activeLaneRef.current);
    const lane = getLaneBoundsFromRef().find((item) => item.index === laneIndex);
    if (lane && action.type !== "cancel" && activeStrokeMetaRef.current?.tool === "pen") {
      appendPointerSamples(nativeEvent, lane, { final: true });
    }
    if (drawRafRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = null;
    }
    flushBufferedDrawPoints();
    resetActivePointerState();
    setIsDrawing(false);
    setSlatePointerLock(false);
    if (activeStrokeMetaRef.current?.tool === "pen") {
      commitActivePenStroke();
    } else {
      activeStrokeMetaRef.current = null;
      activeStrokePointsRef.current = [];
      activeStrokePointProcessorRef.current = null;
    }
    setTimeout(() => {
      onSlateStrokeFinished();
    }, 0);
  }

  useEffect(() => {
    const pointerOwnership = pointerOwnershipRef.current;
    return () => {
      if (drawRafRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(drawRafRef.current);
      }
      drawRafRef.current = null;
      drawPendingPointsRef.current = [];
      activeStrokeMetaRef.current = null;
      activeStrokePointsRef.current = [];
      activeStrokePointProcessorRef.current = null;
      activeLaneRef.current = null;
      activePointerIdRef.current = null;
      activePointerTypeRef.current = "";
      pointerOwnership.reset();
    };
  }, []);

  useEffect(() => {
    if (strokeTool !== "scroll") return;
    if (drawRafRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = null;
    }
    drawPendingPointsRef.current = [];
    activeStrokeMetaRef.current = null;
    activeStrokePointsRef.current = [];
    activeStrokePointProcessorRef.current = null;
    pointerOwnershipRef.current.reset();
    resetActivePointerState();
    clearActiveStrokePreview();
    setIsDrawing(false);
    setSlatePointerLock(false);
  }, [clearActiveStrokePreview, resetActivePointerState, setSlatePointerLock, strokeTool]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const active = workTab === "slate";
    document.documentElement.classList.toggle("slate-interaction-mode", active);
    document.body.classList.toggle("slate-interaction-mode", active);
    return () => {
      document.documentElement.classList.remove("slate-interaction-mode");
      document.body.classList.remove("slate-interaction-mode");
    };
  }, [workTab]);

  useEffect(() => {
    syncActiveStrokeCanvas();
    if (activeStrokeMetaRef.current?.tool === "pen" && activeStrokePointsRef.current.length) {
      drawActiveStrokePreview();
      return;
    }
    clearActiveStrokePreview();
  }, [
    clearActiveStrokePreview,
    drawActiveStrokePreview,
    laneHeights,
    slateFullScreen,
    stageSize.height,
    stageSize.width,
    syncActiveStrokeCanvas
  ]);

  function handleRuleDragStart(boundaryIndex, event) {
    event.preventDefault();
    event.stopPropagation();
    if (shouldRejectResizeTouch(event)) {
      return;
    }
    const idx = Number(boundaryIndex);
    const heights = laneHeightsRef.current;
    if (!Number.isFinite(idx) || idx < 0 || idx >= heights.length - 1) return;
    const bounds = getLaneBoundsFromRef();
    const upperLane = bounds.find((item) => item.index === idx);
    if (!upperLane) return;
    let minDelta = MIN_LANE_HEIGHT - upperLane.height;
    let maxDelta = Number.POSITIVE_INFINITY;
    const upperInk = getLaneInkExtents(idx);
    if (upperInk) {
      const upperInkBottomY = upperInk.laneTop + upperInk.maxY;
      minDelta = Math.max(minDelta, upperInkBottomY + RULER_INK_GUARD_PX - upperLane.bottom);
    }
    bounds.forEach((lane) => {
      if (lane.index <= idx) return;
      const ink = getLaneInkExtents(lane.index);
      if (!ink) return;
      const inkTopY = ink.laneTop + ink.minY;
      const inkBottomY = ink.laneTop + ink.maxY;
      minDelta = Math.max(minDelta, inkBottomY + RULER_INK_GUARD_PX - lane.bottom);
      maxDelta = Math.min(maxDelta, inkTopY - RULER_INK_GUARD_PX - lane.top);
    });
    if (event?.currentTarget?.setPointerCapture && Number.isFinite(Number(event?.pointerId))) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (err) {
        // Ignore capture failures on browsers that do not support pointer capture here.
      }
    }
    laneDragRef.current = {
      boundaryIndex: idx,
      startY: Number(event.clientY || 0),
      startHeight: Number(heights[idx] || DEFAULT_LANE_HEIGHT),
      minDelta,
      maxDelta
    };
    setLaneDragBoundary(idx);
    setSlatePointerLock(true);
  }

  useEffect(() => {
    function onMove(event) {
      const drag = laneDragRef.current;
      if (!drag) return;
      if (event?.preventDefault) {
        event.preventDefault();
      }
      const deltaY = Number(event.clientY || 0) - drag.startY;
      const bounds = getLaneBoundsFromRef();
      const upperLane = bounds.find((item) => item.index === drag.boundaryIndex);
      if (!upperLane) return;
      const clampedDelta = Math.max(
        Number(drag.minDelta || MIN_LANE_HEIGHT - upperLane.height),
        Math.min(Number(drag.maxDelta || Number.POSITIVE_INFINITY), deltaY)
      );
      const nextHeight = Math.max(MIN_LANE_HEIGHT, Math.round(drag.startHeight + clampedDelta));
      setLaneHeights((prev) => {
        if (drag.boundaryIndex >= prev.length) return prev;
        const currentHeight = Number(prev[drag.boundaryIndex] || DEFAULT_LANE_HEIGHT);
        if (currentHeight === nextHeight) return prev;
        const next = [...prev];
        next[drag.boundaryIndex] = nextHeight;
        return next;
      });
    }

    function onUp() {
      if (!laneDragRef.current) return;
      laneDragRef.current = null;
      setLaneDragBoundary(null);
      setSlatePointerLock(false);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [setSlatePointerLock]);

  function clearCanvas(syncServer = true) {
    resetTransientActionState();
    mathEditorUserEditedRef.current = false;
    const attemptIdToClear = ocrAttemptIdRef.current;
    if (activeStrokeMetaRef.current?.tool === "pen") {
      activeStrokeMetaRef.current = null;
      activeStrokePointsRef.current = [];
      clearActiveStrokePreview();
    }
    if (drawRafRef.current && typeof window !== "undefined") {
      window.cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = null;
    }
    drawPendingPointsRef.current = [];
    setLines([]);
    linesRef.current = [];
    setRedoStack([]);
    setShowClearConfirm(false);
    if (isCustom && activeCustomQuestionId) {
      try {
        localStorage.removeItem(getDraftStorageKey(activeCustomQuestionId));
      } catch (_) {}
      saveSlateDraft(activeCustomQuestionId, true, { lines: [], laneHeights: [], ocrEditedLatex: "" });
    }
    setTutorLaneIndex(0);
    setTutorChatOpen(false);
    setTutorHintHistory([]);
    activeLaneRef.current = null;
    activePointerIdRef.current = null;
    activePointerTypeRef.current = "";
    pointerOwnershipRef.current.reset();
    setLaneHeights(buildInitialLaneHeights(getAutoLaneCount(slateFullScreen)));
    setLaneDragBoundary(null);
    setAnalysisStatus("");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setAnalysisLaneMap([]);
    setAnalysisFinalCorrect(null);
    setSelectedDiagStep(null);
    setLastAttemptId("");
    setShowFeedback(false);
    setFeedbackSubmitted(false);
    setFeedbackStatus("");
    setFeedbackHelpful("");
    setFeedbackReasons([]);
    setIsPhotoSubmissionActive(false);
    setPhotoSubmissionPages([]);
    setPhotoProcessingPhase("");
    setDetectedStepBoxes([]);
    setRetryAttemptId("");
    setOcrAttemptId("");
    ocrAttemptIdRef.current = "";
    updateOcrEditedLatex("");
    setOcrReady(false);
    updateLiveOcrStatus("Canvas cleared", { force: true });
    resetLiveTrackingState();
    setStepClusters([]);
    stepClustersRef.current = [];
    previousMathClustersCountRef.current = 0;
    clusterDebounceTimersRef.current.forEach((timer) => clearTimeout(timer));
    clusterDebounceTimersRef.current.clear();
    if (slateRef.current) {
      slateRef.current.scrollTo({ left: 0, top: 0, behavior: "auto" });
    }
    if (syncServer && attemptIdToClear) {
      fetch(`${apiBase}/api/attempts/${attemptIdToClear}/ocr-line`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ clearAll: true })
      }).catch(() => { });
    }
  }

  function handleUndoStroke() {
    clearTutorHint();
    setLines((prev) => {
      const removed = prev[prev.length - 1];
      if (!removed) return prev;
      setRedoStack((stack) => [...stack, removed]);
      const next = prev.slice(0, -1);
      linesRef.current = next;
      if (Number.isFinite(Number(removed.laneIndex))) {
        const laneIndex = Number(removed.laneIndex);
        const lineId = getLineIdForLane(laneIndex);
        const hadInk = Boolean(laneInkStateRef.current[lineId]);
        const nowHasInk = laneHasPenInk(laneIndex, next);
        laneInkStateRef.current[lineId] = nowHasInk;
        if (hadInk || nowHasInk) {
          markLaneDirty(laneIndex);
        }
        syncTutorLaneWithInk(next);
      }
      setTimeout(() => onSlateStrokeFinished(), 0);
      return next;
    });
  }

  function handleRedoStroke() {
    clearTutorHint();
    setRedoStack((stack) => {
      const stroke = stack[stack.length - 1];
      if (!stroke) return stack;
      const remaining = stack.slice(0, -1);
      setLines((prev) => {
        const next = [...prev, stroke];
        linesRef.current = next;
        if (Number.isFinite(Number(stroke.laneIndex))) {
          const laneIndex = Number(stroke.laneIndex);
          setTimeout(() => onLaneStrokeFinished(laneIndex), 0);
        }
        setTimeout(() => onSlateStrokeFinished(), 0);
        return next;
      });
      return remaining;
    });
  }

  async function handleSimilar() {
    if (!question) return;
    if (activeStrokeMetaRef.current?.tool === "pen") {
      if (drawRafRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(drawRafRef.current);
        drawRafRef.current = null;
      }
      flushBufferedDrawPoints();
      activeLaneRef.current = null;
      activePointerIdRef.current = null;
      activePointerTypeRef.current = "";
      pointerOwnershipRef.current.reset();
      setIsDrawing(false);
      setSlatePointerLock(false);
      commitActivePenStroke();
    }
    resetTransientActionState();
    setRetryAttemptId("");
    setLoading(true);
    setError("");
    setAnalysisStatus("");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setAnalysisLaneMap([]);
    setSelectedDiagStep(null);

    try {
      const response = await fetch(`${apiBase}/api/question/similar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ frameId: question.frameId })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load similar question");
      }

      const payload = await response.json();
      setQuestion(payload.question);
      setStudentFigureAssist(null);
      setDraftFigureAssist(null);
      setOcrAttemptId("");
      updateOcrEditedLatex("");
      setOcrReady(false);
      setShowMathKeyboard(false);
      setWorkTab("slate");
      updateLiveOcrStatus("", { force: true });
      setStrokeTool("pen");
      setAnalysisStatus("");
      setAnalysisFeedback("");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setAnalysisLaneMap([]);
      setAnalysisFinalCorrect(null);
      setSelectedDiagStep(null);
      setLastAttemptId("");
      setShowFeedback(false);
      setFeedbackSubmitted(false);
      setFeedbackStatus("");
      setFeedbackHelpful("");
      setFeedbackReasons([]);
      setShowClearConfirm(false);
      clearCanvas(false);
    } catch (err) {
      setError(err.message || "Failed to load similar question");
    } finally {
      setLoading(false);
    }
  }

  function hasLowConfidenceLiveRows() {
    const clusters = stepClustersRef.current || [];
    if (clusters.length > 0) {
      for (const cluster of clusters) {
        const lineId = cluster.id;
        const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
        if (ackRevision <= 0) continue;
        const confidence = String(lineConfidenceRef.current[lineId] || "").toLowerCase();
        if (confidence === "low") return true;
      }
    }
    return /\[unclear\]/i.test(String(ocrEditedLatexRef.current || ""));
  }

  async function runFullSlateOcrPipeline(attemptId, statusMessage = "Full-slate OCR completed.") {
    const dataUrl = renderFullSlateImageData(linesRef.current);
    if (!dataUrl) throw new Error("Slate image unavailable for OCR");
    await uploadSlateSnapshot(attemptId, dataUrl);

    const ocrRes = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ dataUrl })
    });

    if (!ocrRes.ok) {
      const body = await ocrRes.json().catch(() => ({}));
      const details =
        body?.details?.error?.message ||
        body?.details?.message ||
        JSON.stringify(body?.details || {});
      throw new Error(details || body.error || "Full-slate OCR failed");
    }

    const ocrPayload = await ocrRes.json();
    const originalLatex = String(ocrPayload?.ocr?.originalLatex || "");
    const normalizedLatex = normalizeOcrLatexForEditor(originalLatex);
    const nextLatex = normalizedLatex || originalLatex;
    updateOcrEditedLatex(nextLatex);
    setOcrReady(Boolean(nextLatex.trim()));
    updateLiveOcrStatus(statusMessage, { force: true });
    return nextLatex;
  }

  // Retained intentionally for fallback/manual OCR flow (hidden from current UI).
  // eslint-disable-next-line no-unused-vars
  async function handleSubmit() {
    if (!question) return;
    if (isOcrSubmitting) return;
    const sessionId = liveSessionRef.current;
    const isCurrentSession = () => sessionId === liveSessionRef.current;
    setIsOcrSubmitting(true);
    setAnalysisStatus("Submitting for OCR...");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setSelectedDiagStep(null);
    setAnalysisFinalCorrect(null);
    setFeedbackStatus("");

    try {
      await finalizeLiveOcrBeforeAnalyze();
      if (!isCurrentSession()) return;
      const attemptId = await ensureLiveAttemptId();
      if (!isCurrentSession()) return;
      const normalizedLatex = await runFullSlateOcrPipeline(attemptId, "Full-slate OCR completed");
      if (!isCurrentSession()) return;
      setOcrAttemptId(attemptId || "");
      updateOcrEditedLatex(normalizedLatex || "");
      setOcrReady(Boolean(String(normalizedLatex || "").trim()));
      setWorkTab("editor");
      setShowMathKeyboard(false);
      setAnalysisStatus("Review before Erwin analyzes.");
      updateLiveOcrStatus("Read full slate", { force: true });
      setShowFeedback(false);
    } catch (err) {
      if (!isCurrentSession()) return;
      setAnalysisStatus("Failed");
      setAnalysisFeedback(err.message || "OCR failed");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setSelectedDiagStep(null);
      setShowFeedback(false);
    } finally {
      if (!isCurrentSession()) return;
      setIsOcrSubmitting(false);
    }
  }

  // Retained intentionally for fallback/manual tutor analysis flow (hidden from current UI).
  // eslint-disable-next-line no-unused-vars
  async function handleConfirmAndAnalyze() {
    if (!ocrAttemptId) return;
    if (isAnalyzing) return;
    const sessionId = liveSessionRef.current;
    const isCurrentSession = () => sessionId === liveSessionRef.current;
    clearTutorHint();
    setLiveOcrFrozen(true);
    setIsAnalyzing(true);
    setAnalysisStatus("Erwin is reviewing your steps...");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setSelectedDiagStep(null);
    setAnalysisFinalCorrect(null);
    setFeedbackStatus("");

    try {
      await finalizeLiveOcrBeforeAnalyze();
      if (!isCurrentSession()) return;
      let finalizedLatex = "";
      if (ocrAttemptId) {
        const finalizeRes = await fetch(`${apiBase}/api/attempts/${ocrAttemptId}/ocr-live/finalize`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            activeLineIds: (stepClustersRef.current || []).map((c) => c.id)
          })
        }).catch(() => null);
        if (!isCurrentSession()) return;
        if (finalizeRes?.ok) {
          const finalizePayload = await finalizeRes.json().catch(() => ({}));
          finalizedLatex = String(finalizePayload?.ocr?.editedLatex || "");
        }
      }

      const localLatex = String(ocrEditedLatexRef.current || "");
      const isSlateTab = workTab === "slate";
      const isManualEdited = Boolean(mathEditorUserEditedRef.current);
      const latexForConfirm = (!isManualEdited || isSlateTab) && finalizedLatex
        ? finalizedLatex
        : String(localLatex || finalizedLatex || "");

      if (finalizedLatex && (!isManualEdited || isSlateTab)) {
        updateOcrEditedLatex(finalizedLatex);
        setOcrReady(Boolean(finalizedLatex.trim()));
      }
      const stepsForConfirm = splitLatexStepsForPayload(latexForConfirm);

      const ocrUpdateRes = await fetch(`${apiBase}/api/attempts/${ocrAttemptId}/ocr`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          editedLatex: latexForConfirm,
          editedSteps: stepsForConfirm,
          confirm: true
        })
      });
      if (!isCurrentSession()) return;
      if (!ocrUpdateRes.ok) {
        const body = await ocrUpdateRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to confirm OCR");
      }
      const ocrUpdatePayload = await ocrUpdateRes.json().catch(() => ({}));
      syncAttemptSlateContext(ocrUpdatePayload?.attempt, { updateLaneMap: true });

      const analyzeRes = await fetch(`${apiBase}/api/attempts/${ocrAttemptId}/analyze-equivalence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!isCurrentSession()) return;
      if (!analyzeRes.ok) {
        const body = await analyzeRes.json().catch(() => ({}));
        const details =
          body?.details?.error?.message ||
          body?.details?.message ||
          JSON.stringify(body?.details || {});
        throw new Error(details || body.error || "Analysis failed");
      }

      const analyzePayload = await analyzeRes.json();
      if (!isCurrentSession()) return;
      syncAttemptSlateContext(analyzePayload?.attempt, { updateLaneMap: true });
      setAnalysisStatus("Analysis complete");
      setLastAttemptId(analyzePayload.attempt._id || "");
      setShowFeedback(false);
      setFeedbackSubmitted(false);
      setFeedbackStatus("");
      setFeedbackHelpful("");
      setFeedbackReasons([]);

      const rawFeedback = analyzePayload.attempt.aiFeedback || "";
      let parsed = null;

      try {
        const trimmed = rawFeedback.trim();
        if (trimmed.startsWith("```")) {
          const withoutFence = trimmed.replace(/^```[a-zA-Z]*\n?/, "").replace(/```$/, "").trim();
          if (withoutFence.startsWith("{")) {
            parsed = JSON.parse(withoutFence);
          }
        } else if (trimmed.startsWith("{")) {
          parsed = JSON.parse(trimmed);
        } else {
          const jsonStart = rawFeedback.indexOf("{");
          const jsonEnd = rawFeedback.lastIndexOf("}");
          if (jsonStart !== -1 && jsonEnd !== -1) {
            parsed = JSON.parse(rawFeedback.slice(jsonStart, jsonEnd + 1));
          }
        }
      } catch (err) {
        parsed = null;
      }

      if (parsed && parsed.analysis) {
        setAnalysisData(parsed.analysis);
        const alignment = Array.isArray(parsed.alignment) ? parsed.alignment : [];
        setAnalysisAlignment(alignment);
        const firstIssue = alignment.find(
          (item) => item?.status && item.status !== "correct" && Number.isFinite(Number(item.student_step))
        );
        setSelectedDiagStep(firstIssue ? Number(firstIssue.student_step) : null);
        setAnalysisFinalCorrect(
          typeof parsed.final_answer_correct === "boolean" ? parsed.final_answer_correct : null
        );
        setAnalysisFeedback("");
      } else {
        setAnalysisData(null);
        setAnalysisAlignment([]);
        setSelectedDiagStep(null);
        setAnalysisFinalCorrect(null);
        setAnalysisFeedback(rawFeedback || "No feedback");
      }
    } catch (err) {
      if (!isCurrentSession()) return;
      setAnalysisStatus("Failed");
      setAnalysisFeedback(err.message || "Analysis failed");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setSelectedDiagStep(null);
    } finally {
      if (!isCurrentSession()) return;
      setLiveOcrFrozen(false);
      setIsAnalyzing(false);
    }
  }

  const handleSubmitQuestion = () => {
    if (isQuestionSubmitted || submittingQuestion) return;
    setExamModal({
      isOpen: true,
      type: "confirm",
      title: "Submit Question Solution?",
      message: `Submit your handwritten solution for ${question?.title || "this problem"} to your tutor? Your steps will be recorded.`,
      confirmText: "Submit Question",
      cancelText: "Keep Working",
      isLocked: false,
      onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
      onConfirm: async () => {
        setSubmittingQuestion(true);
        setExamModal({
          isOpen: true,
          type: "progress",
          title: "Submitting Question...",
          message: "Saving your handwritten solution steps for your tutor...",
          progress: 50,
          isLocked: true,
          confirmText: "OK",
          cancelText: "Cancel",
          onConfirm: null,
          onCancel: null
        });

        try {
          const hasWork = (Array.isArray(linesRef.current) && linesRef.current.length > 0) ||
            Boolean(String(ocrEditedLatexRef.current || ocrEditedLatex || "").trim());

          if (hasWork && (!analysisAlignment || analysisAlignment.length === 0)) {
            try {
              await handleConfirmAndAnalyzeFast();
            } catch (aErr) {
              console.warn("[Practice] Pre-submission auto-analysis warning:", aErr.message);
            }
          }

          if (isCustom && activeCustomQuestionId) {
            saveSlateDraft(activeCustomQuestionId, true);
          }

          const activeSetId = assignmentMeta?.setId || assignmentMeta?._id || customNav?.setId;
          const latexToSubmit = String(ocrEditedLatexRef.current || ocrEditedLatex || "").trim();
          const stepStringsToSubmit = latexToSubmit
            ? latexToSubmit.split(/\n|\\\\/).map((s) => s.trim()).filter(Boolean)
            : [];

          const currentAttemptId = ocrAttemptId || lastAttemptId || retryAttemptId || null;

          const res = await fetch(`${apiBase}/api/attempts/submit-question`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenRef.current || token}`
            },
            body: JSON.stringify({
              setId: activeSetId,
              currentQuestionId: activeCustomQuestionId || question?._id,
              attemptId: currentAttemptId,
              lines: linesRef.current || lines || [],
              laneHeights: laneHeightsRef.current || laneHeights || [],
              stageSize: stageSize,
              ocrEditedLatex: latexToSubmit,
              ocrEditedSteps: stepStringsToSubmit
            })
          });

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || "Failed to submit question");
          }

          setIsQuestionSubmitted(true);
          setExamModal({
            isOpen: true,
            type: "info",
            title: "✓ Question Submitted!",
            message: "Your solution for this problem has been saved and submitted to your tutor. When you finish all questions, use 'Submit Homework' in the top header to turn in your entire assignment.",
            confirmText: "Continue",
            isLocked: false,
            onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
            onConfirm: () => setExamModal((prev) => ({ ...prev, isOpen: false }))
          });
        } catch (err) {
          console.error("Submit question error:", err);
          setExamModal({
            isOpen: true,
            type: "error",
            title: "Submission Error",
            message: err.message || "Failed to submit question. Please try again.",
            confirmText: "Close",
            isLocked: false,
            onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
            onConfirm: () => setExamModal((prev) => ({ ...prev, isOpen: false }))
          });
        } finally {
          setSubmittingQuestion(false);
        }
      }
    });
  };

  const handleSubmitHomework = () => {
    setExamModal({
      isOpen: true,
      type: "confirm",
      title: "Turn In Entire Homework Assignment?",
      message: "Are you ready to submit your full homework assignment to your tutor? Once submitted, your overall homework is turned in for teacher review.",
      confirmText: "Turn In Homework",
      cancelText: "Keep Working",
      isLocked: false,
      onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
      onConfirm: async () => {
        setSubmittingHomework(true);
        setExamModal({
          isOpen: true,
          type: "progress",
          title: "Submitting Homework",
          message: "Saving solutions and evaluating mathematical steps with Erwin AI...",
          progress: 50,
          isLocked: true,
          confirmText: "OK",
          cancelText: "Cancel",
          onConfirm: null,
          onCancel: null
        });

        try {
          // If slate has handwriting or latex but has not been analyzed yet, run analysis
          const hasWork = (Array.isArray(linesRef.current) && linesRef.current.length > 0) ||
            Boolean(String(ocrEditedLatexRef.current || ocrEditedLatex || "").trim());

          if (hasWork && (!analysisAlignment || analysisAlignment.length === 0)) {
            try {
              await handleConfirmAndAnalyzeFast();
            } catch (aErr) {
              console.warn("[Practice] Pre-submission auto-analysis non-blocking warning:", aErr.message);
            }
          }

          if (isCustom && activeCustomQuestionId) {
            saveSlateDraft(activeCustomQuestionId, true);
          }

          const activeSetId = assignmentMeta?.setId || assignmentMeta?._id || customNav?.setId;
          const latexToSubmit = String(ocrEditedLatexRef.current || ocrEditedLatex || "").trim();
          const stepStringsToSubmit = latexToSubmit
            ? latexToSubmit.split(/\n|\\\\/).map((s) => s.trim()).filter(Boolean)
            : [];

          const currentAttemptId = ocrAttemptId || lastAttemptId || retryAttemptId || null;

          const res = await fetch(`${apiBase}/api/attempts/submit-homework`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenRef.current || token}`
            },
            body: JSON.stringify({
              setId: activeSetId,
              currentQuestionId: activeCustomQuestionId || question?._id,
              attemptId: currentAttemptId,
              lines: linesRef.current || lines || [],
              laneHeights: laneHeightsRef.current || laneHeights || [],
              stageSize: stageSize,
              ocrEditedLatex: latexToSubmit,
              ocrEditedSteps: stepStringsToSubmit
            })
          });

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || "Failed to submit homework");
          }

          setIsHomeworkSubmitted(true);
          setIsQuestionSubmitted(true);
          setExamModal({
            isOpen: true,
            type: "info",
            title: "✓ Homework Submitted!",
            message: "Your full homework assignment has been turned in to your tutor! Your solutions and diagnostics are now visible on your dashboard.",
            confirmText: "View My Tutor",
            isLocked: false,
            onCancel: () => setExamModal((prev) => ({ ...prev, isOpen: false })),
            onConfirm: () => {
              setExamModal((prev) => ({ ...prev, isOpen: false }));
              navigate("/my-tutor");
            }
          });
        } catch (err) {
          setExamModal({
            isOpen: true,
            type: "error",
            title: "Submission Error",
            message: err.message || "Failed to submit homework. Please try again.",
            confirmText: "Close",
            isLocked: false,
            onConfirm: () => setExamModal((prev) => ({ ...prev, isOpen: false }))
          });
        } finally {
          setSubmittingHomework(false);
        }
      }
    });
  };

  async function handleConfirmAndAnalyzeFast() {
    if (!question) return;
    if (isAnalyzing) return;
    const sessionId = liveSessionRef.current;
    const isCurrentSession = () => sessionId === liveSessionRef.current;
    clearTutorHint();
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((timer, clusterId) => {
        clearTimeout(timer);
        const cluster = (stepClustersRef.current || []).find((c) => c.id === clusterId);
        if (cluster) {
          enqueueClusterOcr(cluster, { delay: 0, force: true, allowWhileFrozen: true });
        }
      });
      clusterDebounceTimersRef.current.clear();
    }
    setLiveOcrFrozen(true);
    setIsAnalyzing(true);
    setAnalysisStatus("Erwin is reviewing your steps...");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setSelectedDiagStep(null);
    setAnalysisFinalCorrect(null);
    setFeedbackStatus("");
    updateLiveOcrStatus("", { force: true });
    setShowClearConfirm(false);

    try {
      const attemptId = await ensureLiveAttemptId();
      if (!isCurrentSession()) return;
      if (!attemptId) throw new Error("Unable to initialize attempt");
      setOcrAttemptId(attemptId);

      const slateSnapshot = renderFullSlateImageData(linesRef.current);
      if (slateSnapshot) {
        await uploadSlateSnapshot(attemptId, slateSnapshot);
        if (!isCurrentSession()) return;
      }

      if (
        showFigureAssistPanel
      ) {
        await ensureDraftFigureAssistAccepted({ awaitCompletion: true });
        if (!isCurrentSession()) return;
      }

      await finalizeLiveOcrBeforeAnalyze();
      if (!isCurrentSession()) return;
      let finalizedLatex = "";
      const finalizeRes = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr-live/finalize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          activeLineIds: (stepClustersRef.current || []).map((c) => c.id)
        })
      }).catch(() => null);
      if (!isCurrentSession()) return;
      if (finalizeRes?.ok) {
        const finalizePayload = await finalizeRes.json().catch(() => ({}));
        finalizedLatex = String(finalizePayload?.ocr?.editedLatex || "");
      }

      const localLatex = String(ocrEditedLatexRef.current || "");
      const isSlateTab = workTab === "slate";
      const isManualEdited = Boolean(mathEditorUserEditedRef.current);
      const latexForConfirm = (!isManualEdited || isSlateTab) && finalizedLatex
        ? finalizedLatex
        : String(localLatex || finalizedLatex || "");

      if (finalizedLatex && (!isManualEdited || isSlateTab)) {
        updateOcrEditedLatex(finalizedLatex);
        setOcrReady(Boolean(finalizedLatex.trim()));
      }
      const needsFullSlateFallback = hasLowConfidenceLiveRows();
      const finalLatex = needsFullSlateFallback
        ? await runFullSlateOcrPipeline(
          attemptId,
          "Low-confidence row detected; fallback full-slate OCR completed."
        )
        : latexForConfirm;
      if (!String(finalLatex || "").trim()) {
        const hasAnySlateInk = (linesRef.current || []).some((stroke) => Array.isArray(stroke?.points) && stroke.points.length >= 2);
        if (!hasAnySlateInk) throw new Error("No work available to analyze");
      }
      const stepsForConfirm = splitLatexStepsForPayload(finalLatex);


      const ocrUpdateRes = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          editedLatex: finalLatex,
          editedSteps: stepsForConfirm,
          confirm: true
        })
      });
      if (!isCurrentSession()) return;
      if (!ocrUpdateRes.ok) {
        const body = await ocrUpdateRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to confirm OCR");
      }
      const ocrUpdatePayload = await ocrUpdateRes.json().catch(() => ({}));
      syncAttemptSlateContext(ocrUpdatePayload?.attempt, { updateLaneMap: true });

      const analyzeRes = await fetch(`${apiBase}/api/attempts/${attemptId}/analyze-equivalence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!isCurrentSession()) return;
      if (!analyzeRes.ok) {
        const body = await analyzeRes.json().catch(() => ({}));
        const details =
          body?.details?.error?.message ||
          body?.details?.message ||
          JSON.stringify(body?.details || {});
        throw new Error(details || body.error || "Analysis failed");
      }

      const analyzePayload = await analyzeRes.json();
      if (!isCurrentSession()) return;
      setAnalysisStatus("Analysis complete");
      setLastAttemptId(analyzePayload.attempt._id || "");
      setShowFeedback(false);
      setFeedbackSubmitted(false);
      setFeedbackStatus("");
      setFeedbackHelpful("");
      setFeedbackReasons([]);

      const rawFeedback = analyzePayload.attempt.aiFeedback || "";
      let parsed = null;
      try {
        const trimmed = rawFeedback.trim();
        if (trimmed.startsWith("{")) {
          parsed = JSON.parse(trimmed);
        } else {
          const jsonStart = rawFeedback.indexOf("{");
          const jsonEnd = rawFeedback.lastIndexOf("}");
          if (jsonStart !== -1 && jsonEnd !== -1) {
            parsed = JSON.parse(rawFeedback.slice(jsonStart, jsonEnd + 1));
          }
        }
      } catch (err) {
        parsed = null;
      }

      if (parsed && parsed.analysis) {
        setAnalysisData(parsed.analysis);
        const alignment = Array.isArray(parsed.alignment) ? parsed.alignment : [];
        setAnalysisAlignment(alignment);
        const firstIssue = alignment.find(
          (item) => item?.status && item.status !== "correct" && Number.isFinite(Number(item.student_step))
        );
        setSelectedDiagStep(firstIssue ? Number(firstIssue.student_step) : null);
        setAnalysisFinalCorrect(
          typeof parsed.final_answer_correct === "boolean" ? parsed.final_answer_correct : null
        );
        setAnalysisFeedback("");
      } else {
        setAnalysisData(null);
        setAnalysisAlignment([]);
        setSelectedDiagStep(null);
        setAnalysisFinalCorrect(null);
        setAnalysisFeedback(rawFeedback || "No feedback");
      }
    } catch (err) {
      if (!isCurrentSession()) return;
      setAnalysisStatus("Failed");
      setAnalysisFeedback(err.message || "Analysis failed");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setSelectedDiagStep(null);
    } finally {
      if (!isCurrentSession()) return;
      setLiveOcrFrozen(false);
      setIsAnalyzing(false);
    }
  }

  async function handlePhotoSolutionSubmit(pages) {
    if (!pages || pages.length === 0) return;
    if (isAnalyzing || isPhotoSubmitting) return;

    setIsPhotoSubmitting(true);
    setIsPhotoModalOpen(false);
    setIsPhotoSubmissionActive(true);
    setPhotoSubmissionPages(pages);
    setPhotoProcessingPhase("uploading");
    setDetectedStepBoxes([]);
    setWorkTab("slate");

    clearTutorHint();
    setLiveOcrFrozen(true);
    setIsAnalyzing(true);
    setAnalysisStatus("Saving pages to cloud storage...");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setSelectedDiagStep(null);
    setAnalysisFinalCorrect(null);
    setFeedbackStatus("");
    updateLiveOcrStatus("Uploading notebook photos...", { force: true });
    setShowClearConfirm(false);

    try {
      const attemptId = await ensureLiveAttemptId();
      if (!attemptId) throw new Error("Unable to initialize attempt");
      setOcrAttemptId(attemptId);

      // 1. Upload pages to backend GCS bucket
      const uploadRes = await fetch(`${apiBase}/api/uploads/solution-photo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attemptId,
          photos: pages.map((p, idx) => ({
            dataUrl: p.dataUrl,
            pageNumber: p.pageNumber || idx + 1,
            fileName: p.fileName || `page_${idx + 1}.jpg`
          }))
        })
      });

      let uploadedSolutionImages = [];
      if (uploadRes.ok) {
        const uploadPayload = await uploadRes.json().catch(() => ({}));
        uploadedSolutionImages = uploadPayload.uploaded || [];
      }

      // 2. Call Gemini Flash OCR for continuous multi-page extraction & auto-confirm
      setPhotoProcessingPhase("ocr");
      setAnalysisStatus("Gemini Flash is analyzing handwriting & detecting step boxes...");
      updateLiveOcrStatus("Detecting steps with Gemini Flash...", { force: true });
      const ocrRes = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          images: pages.map((p) => p.dataUrl),
          submissionType: "photo_upload",
          isPhotoUpload: true,
          uploadedSolutionImages,
          confirm: true
        })
      });

      if (!ocrRes.ok) {
        const body = await ocrRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to transcribe notebook photos");
      }

      const ocrPayload = await ocrRes.json().catch(() => ({}));
      const extractedLatex = ocrPayload?.ocr?.originalLatex || "";
      if (extractedLatex) {
        updateOcrEditedLatex(extractedLatex);
        setOcrReady(true);
      }
      if (Array.isArray(ocrPayload?.detectedStepBoxes) && ocrPayload.detectedStepBoxes.length > 0) {
        setDetectedStepBoxes(ocrPayload.detectedStepBoxes);
      }

      // Keep user on the continuous photo slate viewport with live bounding boxes!
      setPhotoProcessingPhase("grading");
      setAnalysisStatus("Gemini Pro is evaluating step-by-step equivalence and grading...");
      updateLiveOcrStatus("Gemini Pro is grading steps...", { force: true });

      // 3. Call Equivalence & Gemini Pro grading
      const analyzeRes = await fetch(`${apiBase}/api/attempts/${attemptId}/analyze-equivalence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (!analyzeRes.ok) {
        const body = await analyzeRes.json().catch(() => ({}));
        const details =
          body?.details?.error?.message ||
          body?.details?.message ||
          JSON.stringify(body?.details || {});
        throw new Error(details || body.error || "Analysis failed");
      }

      const analyzePayload = await analyzeRes.json();
      syncAttemptSlateContext(analyzePayload?.attempt, { updateLaneMap: false });
      if (Array.isArray(analyzePayload?.attempt?.detectedStepBoxes) && analyzePayload.attempt.detectedStepBoxes.length > 0) {
        setDetectedStepBoxes(analyzePayload.attempt.detectedStepBoxes);
      }
      setPhotoProcessingPhase("complete");
      setAnalysisStatus("Analysis complete");
      setLastAttemptId(analyzePayload.attempt?._id || attemptId);
      setShowFeedback(false);
      setFeedbackSubmitted(false);
      setFeedbackStatus("");
      setFeedbackHelpful("");
      setFeedbackReasons([]);

      const rawFeedback = analyzePayload.attempt?.aiFeedback || "";
      let parsed = null;
      try {
        const trimmed = rawFeedback.trim();
        if (trimmed.startsWith("{")) {
          parsed = JSON.parse(trimmed);
        } else {
          const jsonStart = rawFeedback.indexOf("{");
          const jsonEnd = rawFeedback.lastIndexOf("}");
          if (jsonStart !== -1 && jsonEnd !== -1) {
            parsed = JSON.parse(rawFeedback.slice(jsonStart, jsonEnd + 1));
          }
        }
      } catch (err) {
        parsed = null;
      }

      if (parsed && parsed.analysis) {
        setAnalysisData(parsed.analysis);
        const alignment = Array.isArray(parsed.alignment) ? parsed.alignment : [];
        setAnalysisAlignment(alignment);
        const firstIssue = alignment.find(
          (item) => item?.status && item.status !== "correct" && Number.isFinite(Number(item.student_step))
        );
        setSelectedDiagStep(firstIssue ? Number(firstIssue.student_step) : null);
        setAnalysisFinalCorrect(
          typeof parsed.final_answer_correct === "boolean" ? parsed.final_answer_correct : null
        );
        setAnalysisFeedback("");
      } else {
        setAnalysisData(null);
        setAnalysisAlignment([]);
        setSelectedDiagStep(null);
        setAnalysisFinalCorrect(null);
        setAnalysisFeedback(rawFeedback || "No feedback");
      }
    } catch (err) {
      setPhotoProcessingPhase("");
      setAnalysisStatus("Failed");
      setAnalysisFeedback(err.message || "Photo analysis failed");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setSelectedDiagStep(null);
    } finally {
      setIsPhotoSubmitting(false);
      setLiveOcrFrozen(false);
      setIsAnalyzing(false);
    }
  }

  async function handleRequestHint(options = {}) {
    const hintIntent = options?.intent || "next_step";
    if (!question) return;
    if (isAnalyzing || isHinting || isOcrSubmitting) return;
    const sessionId = liveSessionRef.current;
    const isCurrentSession = () => sessionId === liveSessionRef.current;
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((timer, clusterId) => {
        clearTimeout(timer);
        const cluster = (stepClustersRef.current || []).find((c) => c.id === clusterId);
        if (cluster) {
          enqueueClusterOcr(cluster, { delay: 0, force: true, allowWhileFrozen: true });
        }
      });
      clusterDebounceTimersRef.current.clear();
    }
    setLiveOcrFrozen(true);
    setHintingIntent(hintIntent);
    setIsHinting(true);
    setTutorChatOpen(true);
    setTutorHintData(null);
    setShowClearConfirm(false);

    try {
      const attemptId = await ensureLiveAttemptId();
      if (!isCurrentSession()) return;
      if (!attemptId) throw new Error("Unable to initialize attempt");
      setOcrAttemptId(attemptId);

      if (showFigureAssistPanel) {
        await ensureDraftFigureAssistAccepted({ awaitCompletion: true });
        if (!isCurrentSession()) return;
      }

      await finalizeLiveOcrBeforeAnalyze();
      if (!isCurrentSession()) return;

      let finalizedLatex = "";
      const finalizeRes = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr-live/finalize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          activeLineIds: (stepClustersRef.current || []).map((c) => c.id)
        })
      }).catch(() => null);
      if (!isCurrentSession()) return;
      if (finalizeRes?.ok) {
        const finalizePayload = await finalizeRes.json().catch(() => ({}));
        finalizedLatex = String(finalizePayload?.ocr?.editedLatex || "");
      }

      const localLatex = String(ocrEditedLatexRef.current || "");
      const isSlateTab = workTab === "slate";
      const isManualEdited = Boolean(mathEditorUserEditedRef.current);
      const nextLatex = (!isManualEdited || isSlateTab) && finalizedLatex
        ? finalizedLatex
        : (localLatex || finalizedLatex || "");

      if (finalizedLatex && (!isManualEdited || isSlateTab)) {
        updateOcrEditedLatex(finalizedLatex);
        setOcrReady(Boolean(finalizedLatex.trim()));
      }

      const stepsForHint = splitLatexStepsForPayload(nextLatex);


      const ocrUpdateRes = await fetch(`${apiBase}/api/attempts/${attemptId}/ocr`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          editedLatex: nextLatex,
          editedSteps: stepsForHint
        })
      });
      if (!isCurrentSession()) return;
      if (!ocrUpdateRes.ok) {
        const body = await ocrUpdateRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to sync current OCR text");
      }
      const ocrUpdatePayload = await ocrUpdateRes.json().catch(() => ({}));
      syncAttemptSlateContext(ocrUpdatePayload?.attempt, { updateLaneMap: true });

      const previousHintsPayload = (Array.isArray(tutorHintHistory) ? tutorHintHistory : [])
        .slice(-4)
        .map((entry) => {
          const t = entry?.tutor || {};
          return {
            title: t?.summary?.title || t?.guidance?.hint?.title || "",
            message: t?.summary?.message || t?.guidance?.hint?.message || "",
            status: entry?.status || "Hint"
          };
        })
        .filter((h) => h.title || h.message);

      const tutorRes = await fetch(`${apiBase}/api/attempts/${attemptId}/tutor-evaluate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          previousHints: previousHintsPayload,
          intent: hintIntent
        })
      });
      if (!isCurrentSession()) return;
      if (!tutorRes.ok) {
        const body = await tutorRes.json().catch(() => ({}));
        const details = body?.details?.error?.message || body?.details?.message || "";
        throw new Error(details || body.error || "Hint request failed");
      }

      const tutorPayload = await tutorRes.json().catch(() => ({}));
      if (!isCurrentSession()) return;
      syncAttemptSlateContext(tutorPayload?.attempt, { updateLaneMap: true });
      const tutor = tutorPayload?.tutor || null;
      setLastAttemptId(String(tutorPayload?.attempt?._id || ""));
      const nextTutorStatus = tutor?.guidance?.showError && tutor?.guidance?.error
        ? "Error"
        : tutor?.studentState === "complete"
          ? "Done"
          : "Hint";
      setTutorHintData(tutor);
      rememberTutorHint(nextTutorStatus, tutor, { intent: hintIntent });
    } catch (err) {
      if (!isCurrentSession()) return;
      const fallbackTutor = {
        mode: "info",
        summary: {
          title: "Hint unavailable",
          message: err.message || "We could not prepare a hint right now.",
          followUp: null
        },
        targetNode: null,
        blockingIssue: null,
        progress: null
      };
      setTutorHintData(fallbackTutor);
      rememberTutorHint("Error", fallbackTutor, { intent: hintIntent });
    } finally {
      if (!isCurrentSession()) return;
      setLiveOcrFrozen(false);
      setHintingIntent(null);
      setIsHinting(false);
    }
  }

  function toggleReason(reason) {
    setFeedbackReasons((prev) =>
      prev.includes(reason) ? prev.filter((item) => item !== reason) : [...prev, reason]
    );
  }

  async function submitFeedback() {
    if (!lastAttemptId || !feedbackHelpful) return;
    setFeedbackStatus("Submitting feedback...");

    try {
      const response = await fetch(`${apiBase}/api/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          attemptId: lastAttemptId,
          helpful: feedbackHelpful,
          reasons: feedbackHelpful === "yes" ? [] : feedbackReasons
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to submit feedback");
      }

      setFeedbackStatus("Thanks for your feedback!");
      setFeedbackHelpful("");
      setFeedbackReasons([]);
      setFeedbackSubmitted(true);
      setShowFeedback(false);
    } catch (err) {
      setFeedbackStatus(err.message || "Failed to submit feedback");
    }
  }

  function handleTutorBulbClick(opts = {}) {
    if (isAnalyzing || isHinting || isOcrSubmitting) return;
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((timer, clusterId) => {
        clearTimeout(timer);
        const cluster = (stepClustersRef.current || []).find((c) => c.id === clusterId);
        if (cluster) {
          enqueueClusterOcr(cluster, { delay: 0, force: true, allowWhileFrozen: true });
        }
      });
      clusterDebounceTimersRef.current.clear();
    }
    setTutorChatOpen(true);
    handleRequestHint(opts);
  }

  function renderAnalyzeActions(extraClassName = "") {
    if (isTestMode) {
      return (
        <div className={`analysis-runner ${extraClassName}`.trim()}>
          <div className="analysis-runner-actions" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {isExamNotStarted ? (
              <span style={{ fontSize: "12px", color: "#b45309", fontWeight: 600, backgroundColor: "#fef3c7", padding: "4px 10px", borderRadius: "6px", border: "1px solid #fde68a" }}>
                🔒 Exam Starts in {formatTimeLeft(testTimeToStart)}
              </span>
            ) : isExamLocked ? (
              <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                🔒 Exam Submitted & Locked
              </span>
            ) : (
              <button
                id="slate-btn-submit-exam"
                className="primary-button"
                type="button"
                onClick={handleSubmitTest}
                disabled={submittingTest}
                style={{ backgroundColor: "#dc2626", borderColor: "#b91c1c" }}
              >
                {submittingTest ? "Submitting Exam..." : "Submit Exam for Grading"}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={`analysis-runner ${extraClassName}`.trim()}>
        <div className="analysis-runner-actions">
          <button
            id="slate-btn-analyze"
            className="primary-button"
            type="button"
            onClick={handleConfirmAndAnalyzeFast}
            disabled={isAnalyzing || isHinting}
          >
            {isAnalyzing ? "Analyzing..." : "Analyze"}
          </button>
          {isHomework && (
            <button
              id="slate-btn-submit-question"
              className="primary-button"
              type="button"
              onClick={handleSubmitQuestion}
              disabled={submittingQuestion || isQuestionSubmitted}
              style={{
                backgroundColor: isQuestionSubmitted ? "#059669" : "#4f46e5",
                borderColor: isQuestionSubmitted ? "#047857" : "#4338ca",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: isQuestionSubmitted ? "default" : "pointer",
                opacity: isQuestionSubmitted ? 0.9 : 1
              }}
            >
              {submittingQuestion ? "Submitting..." : isQuestionSubmitted ? "✓ Question Submitted" : "Submit Question"}
            </button>
          )}
        </div>
      </div>
    );
  }

  const questionLatex = question?.latex || frame?.latexTemplate || "";
  const questionFigure =
    question?.figureRenderSpec || question?.figureSpec || frame?.figureSpec || null;
  const questionFigurePreviewDataUrl = String(
    question?.figurePreviewDataUrl || frame?.figurePreviewDataUrl || ""
  ).trim();
  const questionPresentation =
    (question?.frameSchema && typeof question.frameSchema === "object" ? question.frameSchema.presentation : null) ||
    (frame?.frameSchema && typeof frame.frameSchema === "object" ? frame.frameSchema.presentation : null) ||
    {};
  const figureAssistLabelHints = useMemo(
    () =>
      extractFigureAssistLabelHints({
        figureSpec: question?.figureSpec || frame?.figureSpec || question?.figureRenderSpec || null,
        diagramSchema: question?.diagramSchema || frame?.diagramSchema || null
      }),
    [frame?.diagramSchema, frame?.figureSpec, question?.diagramSchema, question?.figureRenderSpec, question?.figureSpec]
  );
  const showQuestionFigure =
    Boolean(questionFigure || questionFigurePreviewDataUrl) &&
    (typeof questionPresentation.showQuestionFigure === "boolean"
      ? questionPresentation.showQuestionFigure
      : true);
  const showFigureAssistPanel = false;
  const hasEditableOcrMath = Boolean(String(ocrEditedLatex || "").trim());
  const visibleOcrLaneRows = useMemo(() => {
    if (!hasEditableOcrMath) return ocrLaneRows;
    return ocrLaneRows.filter((row) => row.contentKind !== "math");
  }, [hasEditableOcrMath, ocrLaneRows]);
  const hasVisibleOcrLaneRows = visibleOcrLaneRows.length > 0;
  const figureAssistResetKey = `${question?.id || question?.latex || "none"}:${currentFrameOrder || "0"}`;
  const tutorModeMeta = getTutorModeMeta(String(tutorHintData?.mode || ""));

  useEffect(() => {
    setQuestionPanelOpen(true);
    setFigurePanelOpen(Boolean(showFigureAssistPanel));
  }, [question?.id, question?.latex, currentFrameOrder, showFigureAssistPanel]);

  const stepDiagnostics = useMemo(() => {
    const map = { ...tutorStepDiagnostics };
    analysisAlignment.forEach((item) => {
      const stepNo = Number(item?.student_step);
      if (!Number.isFinite(stepNo) || stepNo <= 0) return;
      map[stepNo] = {
        status: item.status || "review",
        reason: item.reason || ""
      };
    });
    return map;
  }, [analysisAlignment, tutorStepDiagnostics]);
  const slateRowDiagnostics = useMemo(
    () => {
      if (!showSlateDiagnostics) return [];
      return Object.keys(stepDiagnostics)
        .map((key) => Number(key))
        .filter((stepNo) => Number.isFinite(stepNo) && stepNo > 0)
        .sort((a, b) => a - b)
        .map((stepNo) => {
          const diagnostic = stepDiagnostics[stepNo] || null;
          const statusMeta = getStatusMeta(diagnostic?.status);
          // Use the saved lane map if available: step N maps to analysisLaneMap[N-1].
          // Fall back to stepNo-1 when no map exists (e.g. old attempts).
          const laneIndex =
            analysisLaneMap.length >= stepNo
              ? analysisLaneMap[stepNo - 1]
              : stepNo - 1;
          return {
            laneIndex,
            stepNo,
            diagnostic,
            statusMeta
          };
        })
        .filter((item) => laneBounds.some((lane) => Number(lane.index) === Number(item.laneIndex)));
    },
    [laneBounds, stepDiagnostics, showSlateDiagnostics, analysisLaneMap]
  );
  useEffect(() => {
    if (!showSlateDiagnostics) {
      setSelectedDiagStep(null);
      return;
    }
    if (selectedDiagStep === null) return;
    const exists = slateRowDiagnostics.some((item) => Number(item.stepNo) === Number(selectedDiagStep));
    if (!exists) setSelectedDiagStep(null);
  }, [showSlateDiagnostics, slateRowDiagnostics, selectedDiagStep]);

  useEffect(() => {
    if (!showSlateDiagnostics) return undefined;
    const handleOutsidePointerDown = (event) => {
      const railEl = diagnosticRailRef.current;
      if (!railEl) return;
      if (railEl.contains(event.target)) return;
      setSelectedDiagStep(null);
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [showSlateDiagnostics]);

  if (!token) {
    return (
      <section className="page">
        <h2 className="page-title">Loading...</h2>
      </section>
    );
  }

  if (loading && !question) {
    return (
      <section className="page">
        <h2 className="page-title">Loading question...</h2>
      </section>
    );
  }

  if (error && !question) {
    return (
      <section className="page">
        <h2 className="page-title">Unable to load question</h2>
        <p className="error-text">{error}</p>
      </section>
    );
  }

  const stageCanvasWidth = getEffectiveStageWidth();

  return (
    <section className="page practice-page">
      {error && <div className="error-text">{error}</div>}

      <div className={`collapsible-problem-card ${questionPanelOpen ? "is-open" : "is-collapsed"}`}>
        <div
          className="collapsible-problem-header"
          onClick={() => setQuestionPanelOpen((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setQuestionPanelOpen((prev) => !prev);
            }
          }}
          aria-expanded={questionPanelOpen}
          aria-controls="practice-question-panel"
        >
          <div className="collapsible-problem-header-top">
            <div className="collapsible-problem-header-left">
              {!isDemo && (
                <button
                  type="button"
                  className="collapsible-back-btn icon-only"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (location?.state?.backTo) {
                      navigate(location.state.backTo);
                    } else if (isCustom) {
                      navigate(customNav?.isAssignment ? "/my-questions?tab=homework" : (customNav?.setId ? "/my-questions?tab=groups" : "/my-questions"));
                    } else {
                      navigate(`/chapters/${chapterId}`);
                    }
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  title={isCustom ? "Back to Questions" : "Back"}
                  aria-label="Back"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  <svg viewBox="0 0 24 24" className="back-chevron-svg" aria-hidden="true">
                    <path d="M15 19l-7-7 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              <div className="collapsible-problem-titles">
                <span className="collapsible-chapter-name">
                  {isCustom ? (customNav?.setName || "Custom Problem") : (chapterTitles[chapterId] || chapterId)}
                </span>
                <span className="collapsible-title-sep">•</span>
                <span className="collapsible-question-preview">
                  {isCustom
                    ? (customNav?.totalQuestions > 1
                        ? `Question ${customNav.currentQuestionIndex || 1} of ${customNav.totalQuestions} • ${question?.title || "Problem"}`
                        : (question?.title || "Problem"))
                    : (frame?.title || question?.title || `Question ${currentFrameOrder || 1}`)}
                </span>
              </div>
            </div>

            <div className="collapsible-problem-header-right">
              {isTestMode ? (
                <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      backgroundColor: "#fee2e2",
                      color: "#dc2626",
                      border: "1px solid #fca5a5",
                      fontSize: "11px",
                      fontWeight: "700",
                      padding: "2px 7px",
                      borderRadius: "6px"
                    }}
                  >
                    ⏱️ EXAM {assignmentMeta?.durationMinutes ? `(${assignmentMeta.durationMinutes}m)` : ""}
                  </span>
                  {assignmentMeta?.targetGoal && (
                    <span
                      style={{
                        backgroundColor: "#ede9fe",
                        color: "#6d28d9",
                        border: "1px solid #ddd6fe",
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "2px 7px",
                        borderRadius: "6px"
                      }}
                    >
                      🎯 {formatTestGoalLabel(assignmentMeta.targetGoal)}
                    </span>
                  )}
                  <span
                    style={{
                      backgroundColor: "#eff6ff",
                      color: "#1d4ed8",
                      border: "1px solid #bfdbfe",
                      fontSize: "11px",
                      fontWeight: "600",
                      padding: "2px 7px",
                      borderRadius: "6px"
                    }}
                  >
                    📝 {assignmentMeta?.marks || 10} Marks
                  </span>
                  {isExamNotStarted && testTimeToStart !== null && (
                    <span
                      style={{
                        backgroundColor: "#fef3c7",
                        color: "#92400e",
                        border: "1.5px solid #f59e0b",
                        fontSize: "12px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                        padding: "2px 8px",
                        borderRadius: "6px"
                      }}
                    >
                      ⏳ Starts in {formatTimeLeft(testTimeToStart)}
                    </span>
                  )}
                  {!isExamNotStarted && testTimeRemaining !== null && (
                    <span
                      style={{
                        backgroundColor: testTimeRemaining < 300 ? "#fef2f2" : "#f8fafc",
                        color: testTimeRemaining < 300 ? "#dc2626" : "#0f172a",
                        border: `1.5px solid ${testTimeRemaining < 300 ? "#ef4444" : "#cbd5e1"}`,
                        fontSize: "12px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                        padding: "2px 8px",
                        borderRadius: "6px"
                      }}
                    >
                      ⏳ {formatTimeLeft(testTimeRemaining)}
                    </span>
                  )}
                  {isExamNotStarted ? (
                    <span
                      style={{
                        backgroundColor: "#fef3c7",
                        color: "#92400e",
                        border: "1px solid #fde68a",
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "3px 8px",
                        borderRadius: "6px"
                      }}
                    >
                      🔒 Starts at {assignmentMeta?.startTime ? new Date(assignmentMeta.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Scheduled Time"}
                    </span>
                  ) : isExamLocked ? (
                    <span
                      style={{
                        backgroundColor: "#f1f5f9",
                        color: "#475569",
                        border: "1px solid #cbd5e1",
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "3px 8px",
                        borderRadius: "6px"
                      }}
                    >
                      🔒 Locked
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="primary-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmitTest();
                      }}
                      disabled={submittingTest}
                      style={{
                        backgroundColor: "#dc2626",
                        fontSize: "11px",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {submittingTest ? "Submitting..." : "Submit Exam"}
                    </button>
                  )}
                </div>
              ) : isHomework ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    type="button"
                    className="primary-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubmitHomework();
                    }}
                    disabled={submittingHomework || isHomeworkSubmitted}
                    style={{
                      backgroundColor: isHomeworkSubmitted ? "#059669" : "#1e40af",
                      fontSize: "12px",
                      padding: "5px 12px",
                      borderRadius: "6px",
                      whiteSpace: "nowrap",
                      fontWeight: "700",
                      cursor: isHomeworkSubmitted ? "default" : "pointer",
                      opacity: isHomeworkSubmitted ? 0.9 : 1
                    }}
                  >
                    {submittingHomework ? "Submitting..." : isHomeworkSubmitted ? "✓ Homework Submitted" : "✓ Submit Homework"}
                  </button>
                  <span className={`difficulty-badge level-${String(level || question?.difficulty || "lot").toLowerCase()}`}>
                    {level || question?.difficulty || "LOT"}
                  </span>
                </div>
              ) : (
                <span className={`difficulty-badge level-${String(level || question?.difficulty || "lot").toLowerCase()}`}>
                  {level || question?.difficulty || "LOT"}
                </span>
              )}

              {!isDemo && (
                <div
                  className="collapsible-question-actions"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  {!isCustom ? (
                    <>
                      <button
                        className="collapsible-action-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSimilar();
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        disabled={loading || isAnalyzing}
                        title="Try a similar problem"
                      >
                        <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
                          <path d="M4 4v5h5M20 20v-5h-5M4.93 19.07A10 10 0 0020 9M19.07 4.93A10 10 0 004 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="action-btn-text">Similar</span>
                      </button>
                      <button
                        className="collapsible-action-btn"
                        type="button"
                        disabled={loading || isAnalyzing || nextFrameOrder === null}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (loading || isAnalyzing || nextFrameOrder === null) return;
                          setLoading(true);
                          setRetryAttemptId("");
                          setSelectedFrameOrder(nextFrameOrder);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        title="Next problem in chapter"
                      >
                        <span className="action-btn-text">Next</span>
                        <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
                          <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>
                      {(question?.imageUrl || question?.uploadedImageUrl) && (
                        <button
                          type="button"
                          className="collapsible-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAttachmentModal(true);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          title="View original uploaded question image/PDF"
                        >
                          <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
                            <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                            <path d="M21 15l-5-5L5 21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span className="action-btn-text">Original</span>
                        </button>
                      )}
                      {customNav?.prevQuestionId && (
                        <button
                          className="collapsible-action-btn"
                          type="button"
                          disabled={loading || isAnalyzing}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (loading || isAnalyzing) return;
                            handleNavigateQuestion(customNav.prevQuestionId);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          title="Previous question in assignment"
                        >
                          <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
                            <path d="M15 19l-7-7 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span className="action-btn-text">Prev</span>
                        </button>
                      )}
                      {customNav?.nextQuestionId && (
                        <button
                          className="collapsible-action-btn"
                          type="button"
                          disabled={loading || isAnalyzing}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (loading || isAnalyzing) return;
                            handleNavigateQuestion(customNav.nextQuestionId);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          title="Next question in assignment"
                        >
                          <span className="action-btn-text">Next</span>
                          <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
                            <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </>
                  )}
                  <button
                    className="collapsible-action-btn primary"
                    type="button"
                    disabled={loading || isAnalyzing || isHinting || isOcrSubmitting}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCollabInviteModal();
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    title="Ink Together"
                  >
                    <svg viewBox="0 0 24 24" className="action-icon-svg" aria-hidden="true">
                      <path
                        d="M15 5l4 4m0 0l-4 4m4-4H9a4 4 0 000 8h2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="action-btn-text">Ink Together</span>
                  </button>
                </div>
              )}
              <button
                type="button"
                className="collapsible-collapse-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  setQuestionPanelOpen((prev) => !prev);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                aria-label={questionPanelOpen ? "Collapse problem" : "Expand problem"}
              >
                <span>{questionPanelOpen ? "▲" : "▼"}</span>
              </button>
            </div>
          </div>
        </div>

        <div
          className="collapsible-problem-body"
          id="practice-question-panel"
          hidden={!questionPanelOpen}
          aria-hidden={!questionPanelOpen}
        >
          {questionLatex ? (
            <div className="practice-question-wrap">
              <BlockMath key={question?.id} math={formatLatexForDisplay(questionLatex)} />
            </div>
          ) : (
            <div>No question loaded</div>
          )}
          {showQuestionFigure && (
            <div className="figure-block">
              {questionFigurePreviewDataUrl ? (
                <img
                  className="attempt-figure-image"
                  src={questionFigurePreviewDataUrl}
                  alt="Question figure"
                />
              ) : (
                <FigureRender figureSpec={questionFigure} cropToContent contentPadding={8} theme="accepted" />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="slate-card">
        {isTestMode && isExamLocked && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              margin: "12px 16px",
              color: "#991b1b"
            }}
          >
            <span style={{ fontSize: "22px" }}>🔒</span>
            <div style={{ fontSize: "13px", lineHeight: "1.4" }}>
              <strong>Exam Workspace Locked:</strong>{" "}
              {isTestSubmitted
                ? "You have submitted your exam. Your handwriting and steps are locked and preserved. Your tutor will grade your submission against the curriculum rubric."
                : "The sacrosanct deadline for this timed exam has passed. The workspace is now locked in read-only mode."}
            </div>
          </div>
        )}

        {isTestMode && isExamNotStarted && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: "#fef3c7",
              border: "1.5px solid #fde68a",
              borderRadius: "8px",
              margin: "12px 16px",
              color: "#92400e"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "22px" }}>⏳</span>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 700 }}>
                  Exam Scheduled • Starts at {assignmentMeta?.startTime ? new Date(assignmentMeta.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Scheduled Time"}
                </div>
                <div style={{ fontSize: "12px", color: "#78350f", marginTop: "2px" }}>
                  Duration: {assignmentMeta?.durationMinutes || 60} mins • Drawing on the slate canvas is disabled until the exam start time.
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "2px",
                marginLeft: "16px"
              }}
            >
              <span style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#b45309" }}>
                STARTS IN
              </span>
              <span
                style={{
                  fontWeight: 800,
                  fontFamily: "monospace",
                  fontSize: "16px",
                  color: "#92400e",
                  whiteSpace: "nowrap"
                }}
              >
                {formatTimeLeft(testTimeToStart)}
              </span>
            </div>
          </div>
        )}

        {isTestMode && !isExamLocked && !isExamNotStarted && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              backgroundColor: "#fffbeb",
              border: "1px solid #fef3c7",
              borderRadius: "8px",
              margin: "12px 16px",
              color: "#92400e",
              fontSize: "12px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>⏱️</span>
              <span>
                <strong>Timed Exam Mode ({assignmentMeta?.durationMinutes || 60} mins):</strong> Live Erwin AI hints and step equivalence are disabled for this exam. Write out your complete solution steps on the slate, then click <strong>Submit Exam</strong>.
              </span>
            </div>
            {testTimeRemaining !== null && (
              <span
                style={{
                  fontWeight: 700,
                  fontFamily: "monospace",
                  fontSize: "13px",
                  color: testTimeRemaining < 300 ? "#dc2626" : "#b45309",
                  whiteSpace: "nowrap",
                  marginLeft: "12px"
                }}
              >
                ⏳ {formatTimeLeft(testTimeRemaining)} left
              </span>
            )}
          </div>
        )}

        {showFigureAssistPanel && (
          <div className="practice-fold-section">
            <button
              type="button"
              className="practice-fold-trigger"
              onClick={() => setFigurePanelOpen((prev) => !prev)}
              aria-expanded={figurePanelOpen}
              aria-controls="practice-figure-panel"
            >
              <span className="practice-fold-copy">
                <span className="question-meta">Figure Sketch</span>
                <span className="practice-fold-title">Fold this away to maximize handwriting space</span>
              </span>
              <span className="practice-fold-chevron" aria-hidden="true">
                {figurePanelOpen ? "\u25be" : "\u25b8"}
              </span>
            </button>
            <div
              className="practice-fold-body"
              id="practice-figure-panel"
              hidden={!figurePanelOpen}
              aria-hidden={!figurePanelOpen}
            >
              <FigureAssistPanel
                title="Figure Sketch Capture"
                helperText="Draw the figure here. Near-straight strokes are snapped into geometry segments immediately. Use the pen icon on the top-right to switch between black, blue, solid, and dashed drawing. Rename points if needed; the figure will be used automatically during Analyze."
                resetKey={figureAssistResetKey}
                acceptedStatus={studentFigureAssist?.status || null}
                suggestedPoints={figureAssistLabelHints.suggestedPoints}
                suggestedLabels={figureAssistLabelHints.suggestedLabels}
                enableValueLabels
                showAcceptRejectControls={false}
                onPreviewChange={handlePreviewFigureAssist}
                onAccept={acceptStudentFigureAssist}
                onReject={rejectStudentFigureAssist}
              />
            </div>
          </div>
        )}
        <SlateCanvasPanel
          slateProfile={slateProfile}
          slateFullScreen={slateFullScreen}
          strokeTool={strokeTool}
          showPenMenu={showPenMenu}
          strokeColor={strokeColor}
          lines={lines}
          redoStack={redoStack}
          liveOcrStatus={liveOcrStatus}
          isDrawing={isDrawing}
          showSlateDiagnostics={showSlateDiagnostics && !isTestMode}
          stageCanvasWidth={stageCanvasWidth}
          stageSize={stageSize}
          laneBounds={laneBounds}
          laneDragBoundary={laneDragBoundary}
          slateRowDiagnostics={isTestMode ? [] : slateRowDiagnostics}
          selectedDiagStep={selectedDiagStep}
          toolbarMenuRef={toolbarMenuRef}
          slateRef={slateRef}
          canvasPaneRef={canvasPaneRef}
          stageRef={stageRef}
          activeStrokeCanvasRef={activeStrokeCanvasRef}
          diagnosticRailRef={diagnosticRailRef}
          setStrokeTool={setStrokeTool}
          setShowPenMenu={setShowPenMenu}
          setStrokeColor={setStrokeColor}
          handleUndoStroke={handleUndoStroke}
          handleRedoStroke={handleRedoStroke}
          setSlateFullScreen={setSlateFullScreen}
          handlePointerDown={handlePointerDown}
          handlePointerMove={handlePointerMove}
          handlePointerUp={handlePointerUp}
          handleRuleDragStart={handleRuleDragStart}
          setSelectedDiagStep={setSelectedDiagStep}
          analyzeActions={renderAnalyzeActions()}
          ruleStroke={SLATE_RULE_STROKE}
          ruleStrokeWidth={SLATE_RULE_STROKE_WIDTH}
          ruleDash={SLATE_RULE_DASH}
          diagnosticRailWidth={DIAGNOSTIC_RAIL_WIDTH}
          onClearSlate={() => setShowClearConfirm(true)}
          clearSlateDisabled={isExamLocked || isExamNotStarted || isAnalyzing || isOcrSubmitting}
          showClearConfirm={showClearConfirm}
          onCancelClearSlate={() => setShowClearConfirm(false)}
          onConfirmClearSlate={() => clearCanvas(true)}
          tutorLaneIndex={isTestMode ? null : tutorLaneIndex}
          onTutorHintClick={isTestMode ? undefined : handleTutorBulbClick}
          onSlateScrollNearEnd={() => appendBlankSlateLanes(SLATE_SCROLL_EXTEND_BATCH)}
          tutorHintDisabled={isTestMode || isAnalyzing || isHinting || isOcrSubmitting}
          isHinting={isTestMode ? false : isHinting}
          hintingIntent={isTestMode ? null : hintingIntent}
          tutorChatOpen={isTestMode ? false : tutorChatOpen}
          onTutorChatToggle={isTestMode ? undefined : () => setTutorChatOpen((prev) => !prev)}
          onTutorChatOpen={isTestMode ? undefined : () => setTutorChatOpen(true)}
          tutorHintData={isTestMode ? null : tutorHintData}
          tutorHintHistory={isTestMode ? [] : tutorHintHistory}
          tutorModeMeta={isTestMode ? null : tutorModeMeta}
          plainCanvas={true}
          stepClusters={stepClusters}
          paperStyle={paperStyle}
          setPaperStyle={handleSetPaperStyle}
          workTab={workTab}
          setWorkTab={handleSetWorkTab}
          onOpenPhotoModal={() => setIsPhotoModalOpen(true)}
          isPhotoSubmissionActive={isPhotoSubmissionActive}
          photoSubmissionPages={photoSubmissionPages}
          isAnalyzing={isAnalyzing}
          analysisStatus={analysisStatus}
          photoProcessingPhase={photoProcessingPhase}
          detectedStepBoxes={detectedStepBoxes}
          stepDiagnostics={isTestMode ? {} : stepDiagnostics}
          editorContent={
            <div className="slate-math-editor-inner">
              {isPhotoSubmissionActive && (isAnalyzing || photoProcessingPhase === "grading") && (
                <div className="photo-grading-progress-banner">
                  <div className="photo-grading-progress-spinner" />
                  <div className="photo-grading-progress-info">
                    <div className="photo-grading-progress-title">Transcribed from Notebook Photos</div>
                    <div className="photo-grading-progress-desc">
                      {analysisStatus || "Gemini Pro is evaluating step-by-step equivalence and grading..."}
                    </div>
                  </div>
                </div>
              )}
              <div className="analysis-label-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div className="analysis-label" style={{ margin: 0, fontSize: "14px", fontWeight: "700" }}>OCR Math Editor</div>
                <button
                  type="button"
                  className="math-keyboard-icon"
                  onClick={() => setShowMathKeyboard((prev) => !prev)}
                  title="Toggle math keyboard"
                >
                  {"\u2328"}
                </button>
              </div>
              {hasVisibleOcrLaneRows && (
                <div className="ocr-lane-review">
                  <div className="analysis-label">Slate OCR steps</div>
                  {visibleOcrLaneRows.map((row) => (
                    <div key={row.lineId} className={`ocr-lane-row ocr-lane-row-${row.contentKind}`}>
                      <div className="ocr-lane-row-head">
                        <span>Step {row.lineOrder + 1}</span>
                        <span>{getOcrLaneKindLabel(row.contentKind)}</span>
                        {row.confidence && <span>{row.confidence}</span>}
                      </div>
                      {row.latex && <div className="ocr-lane-latex">{row.latex}</div>}
                      {row.diagramSummaryText && (
                        <div className="ocr-lane-diagram-summary">{row.diagramSummaryText}</div>
                      )}
                      {!row.latex && !row.diagramSummaryText && (
                        <div className="analysis-text">This row is still unclear. Erase and redraw if needed.</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(!hasEditableOcrMath && !ocrReady && !hasVisibleOcrLaneRows) && (
                <div className="analysis-text" style={{ padding: "16px 0", color: "#64748b" }}>
                  {isTestMode
                    ? "Transcribing your handwritten steps via live OCR. Write your steps on the slate or click the keyboard icon above to enter/edit steps."
                    : "OCR lines from slate will appear here for optional editing."}
                </div>
              )}
              {(hasEditableOcrMath || ocrReady) && (
                <>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "6px", display: "block" }}>
                    {isTestMode ? "Exam Solution Steps (Transcribed via Live OCR)" : "Review before Erwin analyzes"}
                  </label>
                  <MathLiveEditor
                    value={ocrEditedLatex}
                    onChange={handleMathLiveEditorChange}
                    showKeyboard={showMathKeyboard}
                    stepDiagnostics={isTestMode ? {} : stepDiagnostics}
                    selectedStep={selectedDiagStep}
                    onStepSelect={setSelectedDiagStep}
                  />
                </>
              )}
              {!hasEditableOcrMath && hasVisibleOcrLaneRows && (
                <div className="analysis-text">
                  No editable math line yet. Diagram facts will still be sent to tutor and analyze.
                </div>
              )}
            </div>
          }
        />
        {analysisStatus && !loading && (
          <div className="analysis-box">
            <div className="question-meta">{analysisStatus}</div>
            {analysisData && (
              <div className="analysis-summary">
                {analysisFinalCorrect === true
                  ? "Correct solution. See details below."
                  : analysisFinalCorrect === false
                    ? "You have some improvement areas. See details below."
                    : analysisData.where_it_went_wrong_or_unclear || analysisData.next_step_suggestion
                      ? "You have some improvement areas. See details below."
                      : "Correct solution. See details below."}
              </div>
            )}
            {analysisData ? (
              <div className="analysis-grid">
                {analysisData.what_went_right && (
                  <div>
                    <div className="analysis-label">What went right</div>
                    <div className="analysis-text">{analysisData.what_went_right}</div>
                  </div>
                )}
                {analysisData.where_it_went_wrong_or_unclear && (
                  <div>
                    <div className="analysis-label">Where it went wrong or unclear</div>
                    <div className="analysis-text">{analysisData.where_it_went_wrong_or_unclear}</div>
                  </div>
                )}
                {analysisData.next_step_suggestion && (
                  <div>
                    <div className="analysis-label">Next step suggestion</div>
                    <div className="analysis-text">{analysisData.next_step_suggestion}</div>
                  </div>
                )}
              </div>
            ) : (
              analysisFeedback && <div className="analysis-text">{analysisFeedback}</div>
            )}
          </div>
        )}
        {!isDemo && analysisStatus === "Analysis complete" && (
          <div className="feedback-submit">
            <button
              className="secondary-button feedback-trigger"
              type="button"
              onClick={() => setShowFeedback(true)}
              disabled={feedbackSubmitted}
            >
              {feedbackSubmitted ? "Feedback submitted" : "Give feedback"}
            </button>
            {feedbackStatus && <div className="question-meta">{feedbackStatus}</div>}
          </div>
        )}
      </div>
      {collabInviteOpen && !isDemo && (
        <div
          className="collab-invite-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Create collaboration invite"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeCollabInviteModal();
          }}
        >
          <div className="collab-invite-modal">
            <div className="collab-invite-modal-header">
              <div>
                <div className="analysis-label">Shared solving</div>
                <h3>Let's solve together</h3>
              </div>
              <button
                type="button"
                className="feedback-close"
                onClick={closeCollabInviteModal}
              >
                Close
              </button>
            </div>

            <p className="question-meta">
              Search by username to send an in-app invite, or type an email address. If the email is not already in InkSolver, you can still create the room and share it manually.
            </p>

            <label className="collab-invite-field">
              <span>Friend username or email</span>
              <input
                value={collabInviteTarget}
                onChange={handleCollabInviteTargetChange}
                placeholder="Start typing a username or enter an email"
                disabled={collabInviteBusy}
                autoComplete="off"
              />
            </label>

            {collabInviteLookupBusy && <div className="question-meta">Looking up students...</div>}

            {collabInviteSelectedUser && (
              <div className="collab-invite-match-card">
                <div className="collab-invite-match-head">
                  <span className="analysis-label">Selected student</span>
                  <button
                    type="button"
                    className="tool-chip"
                    onClick={() => {
                      setCollabInviteSelectedUser(null);
                      setCollabInviteResolvedUser(null);
                    }}
                    disabled={collabInviteBusy}
                  >
                    Clear
                  </button>
                </div>
                <div className="collab-invite-match-title">
                  @{collabInviteSelectedUser.username || "student"}
                </div>
                <div className="question-meta">
                  {collabInviteSelectedUser.name || "Student"}
                  {collabInviteSelectedUser.email ? ` • ${collabInviteSelectedUser.email}` : ""}
                </div>
              </div>
            )}

            {!collabInviteSelectedUser && collabInviteLookupMode === "username" && collabInviteMatches.length > 0 && (
              <div className="collab-invite-suggestions" role="listbox" aria-label="Matching students">
                {collabInviteMatches.map((match) => (
                  <button
                    key={match.uid || match.username}
                    type="button"
                    className="collab-invite-suggestion"
                    onClick={() => handleCollabInviteUserSelect(match)}
                  >
                    <span className="collab-invite-suggestion-name">@{match.username || "student"}</span>
                    <span className="collab-invite-suggestion-meta">
                      {match.name || "Student"}
                      {match.email ? ` • ${match.email}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {collabInviteLookupNote && !collabInviteError && (
              <div className="question-meta">{collabInviteLookupNote}</div>
            )}

            {collabInviteError && <div className="error-text">{collabInviteError}</div>}

            {collabInviteShowShareRail && (
              <div className="collab-share-shortcuts">
                <div className="collab-share-shortcuts-copy">
                  <div className="analysis-label">Share options</div>
                  <div className="question-meta">
                    {collabInviteShareReady
                      ? collabInviteHasMatchedUser
                        ? "This student can be notified in-app and you can also share the room manually."
                        : "Share the room by link, Gmail, or WhatsApp."
                      : "Create the room once to enable these share actions."}
                  </div>
                </div>
                <button
                  className="secondary-button collab-share-shortcut native-shortcut"
                  type="button"
                  onClick={handleCollabNativeShare}
                  disabled={!collabInviteShareReady}
                  aria-label="Share room link"
                  title={collabInviteShareReady ? "Share room link" : "Create the room first"}
                >
                  <span className="collab-share-shortcut-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="slate-icon-svg">
                      <path
                        d="M15 5l4 4m0 0l-4 4m4-4H9a4 4 0 000 8h2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                <button
                  className="secondary-button collab-share-shortcut gmail-shortcut"
                  type="button"
                  onClick={handleCollabMailShare}
                  disabled={!collabInviteShareReady}
                  aria-label="Share by Gmail"
                  title={collabInviteShareReady ? "Share by Gmail" : "Create the room first"}
                >
                  <span className="collab-share-shortcut-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="slate-icon-svg">
                      <path
                        d="M3 7.5l9 6 9-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <rect
                        x="3"
                        y="6"
                        width="18"
                        height="12"
                        rx="2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                    </svg>
                  </span>
                </button>
                <button
                  className="secondary-button collab-share-shortcut whatsapp-shortcut"
                  type="button"
                  onClick={handleCollabWhatsAppShare}
                  disabled={!collabInviteShareReady}
                  aria-label="Share by WhatsApp"
                  title={collabInviteShareReady ? "Share by WhatsApp" : "Create the room first"}
                >
                  <span className="collab-share-shortcut-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="slate-icon-svg">
                      <path
                        d="M12 4a7.5 7.5 0 00-6.4 11.4L5 20l4.8-1.3A7.5 7.5 0 1012 4z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9.7 9.1c.2-.4.5-.4.7-.4h.5c.2 0 .4.1.5.4l.6 1.5c.1.2.1.4 0 .6l-.4.7c.5.9 1.2 1.6 2.1 2.1l.7-.4c.2-.1.4-.1.6 0l1.5.6c.3.1.4.3.4.5v.5c0 .2 0 .5-.4.7-.4.2-1 .3-1.5.2-2.8-.5-5.1-2.8-5.6-5.6-.1-.5 0-1.1.2-1.4z"
                        fill="currentColor"
                        stroke="none"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            )}

            {collabInviteUrl && (
              <div className="collab-share-link">
                <div className="analysis-label">
                  {collabInviteHasMatchedUser ? "Notification sent" : "Invite link ready"}
                </div>
                <div>{collabInviteUrl}</div>
              </div>
            )}

            <div className="collab-invite-actions">
              <button className="primary-button" type="button" onClick={createCollaborationRoom} disabled={collabInviteBusy}>
                {collabInviteBusy
                  ? "Creating room..."
                  : collabInviteHasMatchedUser || collabInviteSelectedUser
                    ? "Create room, notify, and share"
                    : "Create room and share"}
              </button>
              {collabInviteUrl && (
                <a className="secondary-button" href={collabInviteUrl} target="_blank" rel="noreferrer">
                  Open shared room
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {showFeedback && (
        <div className="feedback-modal">
          <div className="feedback-card">
            <div className="feedback-card-header">
              <div className="feedback-title">Was this feedback helpful?</div>
              <button
                type="button"
                className="feedback-close"
                onClick={() => setShowFeedback(false)}
              >
                Close
              </button>
            </div>
            <div className="feedback-options">
              <button
                type="button"
                className={`feedback-pill ${feedbackHelpful === "yes" ? "active" : ""}`}
                onClick={() => setFeedbackHelpful("yes")}
              >
                Yes :)
              </button>
              <button
                type="button"
                className={`feedback-pill ${feedbackHelpful === "somewhat" ? "active" : ""}`}
                onClick={() => setFeedbackHelpful("somewhat")}
              >
                Somewhat :|
              </button>
              <button
                type="button"
                className={`feedback-pill ${feedbackHelpful === "no" ? "active" : ""}`}
                onClick={() => setFeedbackHelpful("no")}
              >
                No :(
              </button>
            </div>
            {feedbackHelpful && feedbackHelpful !== "yes" && (
              <div className="feedback-reasons">
                <div className="feedback-subtitle">
                  What could be better? (check all that apply)
                </div>
                {[
                  "AI was wrong",
                  "Too strict/ nitpicky",
                  "Too obvious/ unnecessary",
                  "Explanation was confusing",
                  "It didn't read my handwriting correctly",
                  "I already knew this",
                  "Question itself was unclear"
                ].map((reason) => (
                  <label key={reason} className="reason-option">
                    <input
                      type="checkbox"
                      checked={feedbackReasons.includes(reason)}
                      onChange={() => toggleReason(reason)}
                    />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="feedback-actions">
              <button className="primary-button" type="button" onClick={submitFeedback}>
                Submit feedback
              </button>
              {feedbackStatus && <div className="question-meta">{feedbackStatus}</div>}
            </div>
          </div>
        </div>
      )}
      {showAttachmentModal && (question?.imageUrl || question?.uploadedImageUrl) && (
        <div
          className="modal-overlay"
          onClick={() => setShowAttachmentModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.7)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            backdropFilter: "blur(2px)"
          }}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxWidth: "min(840px, calc(100vw - 32px))",
              maxHeight: "calc(100vh - 48px)",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 18px",
                borderBottom: "1px solid var(--border)",
                backgroundColor: "#f8fafc"
              }}
            >
              <div style={{ fontWeight: "700", fontSize: "14px", color: "var(--foreground)" }}>
                Original Question
              </div>
              <button
                type="button"
                onClick={() => setShowAttachmentModal(false)}
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1.5px solid #ef4444",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                  color: "#b91c1c",
                  padding: "5px 12px",
                  lineHeight: "1.2",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px"
                }}
                title="Close Window"
                aria-label="Close Window"
              >
                <span>✕</span>
                <span>Close</span>
              </button>
            </div>
            <div
              style={{
                padding: "16px",
                overflowY: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f1f5f9"
              }}
            >
              <img
                src={question.imageUrl || question.uploadedImageUrl}
                alt="Original Question"
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 160px)",
                  borderRadius: "8px",
                  objectFit: "contain",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                }}
              />
            </div>
          </div>
        </div>
      )}

      <PhotoSolutionModal
        isOpen={isPhotoModalOpen}
        onClose={() => setIsPhotoModalOpen(false)}
        onSubmitPhotos={handlePhotoSolutionSubmit}
        isSubmitting={isPhotoSubmitting}
      />

      {examModal.isOpen && (
        <ActionModal
          isOpen={examModal.isOpen}
          type={examModal.type}
          title={examModal.title}
          message={examModal.message}
          progress={examModal.progress}
          progressLabel={examModal.progressLabel}
          detail={examModal.detail}
          isLocked={examModal.isLocked}
          confirmText={examModal.confirmText}
          cancelText={examModal.cancelText}
          onConfirm={examModal.onConfirm}
          onCancel={examModal.onCancel}
        />
      )}
    </section>
  );
}

export default Practice;

